import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, Clock, Users, Copy, X, Edit2, Trash2, Send, AlertCircle, Check, Plane, Plus, LayoutList, Tag, Building, Settings, ArrowUp, ArrowDown, ListOrdered, Coffee, Bell, GripVertical, MessageSquare, Paintbrush } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import WeatherBadge from "../components/WeatherBadge";
import WeatherDetailModal from "../components/WeatherDetailModal";
import { fetchWeekWeather, getSavedLocation } from "../services/weatherService";
import { useAuth } from "../AuthContext";
import { useNotifications } from "../NotificationContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import API_URL from '../config';
import { parseDateSafe, formatDateSafe, formatTimeSafe, getCalendarDayOffset, toCalendarDateKey, formatDecimalHours } from '../utils/dateUtils';

const SortableUserItem = ({ id, user, index, moveUser, isFirst, isLast, colorMatch }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow group ${isDragging ? 'ring-2 ring-indigo-500 shadow-lg opacity-90' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
      >
        <GripVertical className="w-5 h-5" />
      </div>

      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => moveUser(index, 'up')}
          disabled={isFirst}
          className={`p-1 rounded ${isFirst ? 'text-gray-300' : 'text-indigo-600 hover:bg-indigo-100'}`}
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => moveUser(index, 'down')}
          disabled={isLast}
          className={`p-1 rounded ${isLast ? 'text-gray-300' : 'text-indigo-600 hover:bg-indigo-100'}`}
        >
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>

      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colorMatch.gradient} flex items-center justify-center text-white text-xs font-semibold`}>
        {(user.full_name || user.email).charAt(0).toUpperCase()}
      </div>

      <span className="font-medium text-gray-700">
        {user.full_name || user.email}
      </span>
    </div>
  );
};

const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const daysShort = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Palette de couleurs pour différencier les employés
const employeeColors = [
  { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", gradient: "from-indigo-500 to-indigo-600" },
  { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", gradient: "from-purple-500 to-purple-600" },
  { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-300", gradient: "from-pink-500 to-pink-600" },
  { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-300", gradient: "from-rose-500 to-rose-600" },
  { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", gradient: "from-orange-500 to-orange-600" },
  { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300", gradient: "from-amber-500 to-amber-600" },
  { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300", gradient: "from-emerald-500 to-emerald-600" },
  { bg: "bg-teal-100", text: "text-teal-700", border: "border-teal-300", gradient: "from-teal-500 to-teal-600" },
  { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-300", gradient: "from-cyan-500 to-cyan-600" },
  { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-300", gradient: "from-sky-500 to-sky-600" },
];

// ── Leave datetime helpers ────────────────────────────────────────────────
function buildLeaveDatetimes(startDate, endDate, period) {
  // period for single-day: 'full' | 'morning' | 'afternoon'
  // period for multi-day: { startFull: bool, endFull: bool }
  if (startDate === endDate) {
    if (period === 'morning')
      return { start_datetime: `${startDate}T00:00:00`, end_datetime: `${endDate}T12:00:00` };
    if (period === 'afternoon')
      return { start_datetime: `${startDate}T12:00:00`, end_datetime: `${endDate}T23:59:00` };
    return { start_datetime: `${startDate}T00:00:00`, end_datetime: `${endDate}T23:59:00` };
  }
  const startTime = period.startFull ? 'T00:00:00' : 'T12:00:00';
  const endTime   = period.endFull   ? 'T23:59:00' : 'T12:00:00';
  return { start_datetime: startDate + startTime, end_datetime: endDate + endTime };
}

function getLeavePeriod(start_datetime, end_datetime) {
  const startHour = parseDateSafe(start_datetime).getHours();
  const endHour   = parseDateSafe(end_datetime).getHours();
  const sameDay = (toCalendarDateKey(start_datetime) || start_datetime) === (toCalendarDateKey(end_datetime) || end_datetime);
  if (sameDay) {
    if (startHour === 0  && endHour === 12) return 'morning';
    if (startHour === 12 && endHour !== 12) return 'afternoon';
  }
  return 'full';
}

function getLeaveHalves(leave, dateStr) {
  const leaveStartDate = toCalendarDateKey(leave.start_datetime);
  const leaveEndDate = toCalendarDateKey(leave.end_datetime);
  const isSingleDay    = leaveStartDate === leaveEndDate;
  if (isSingleDay && dateStr === leaveStartDate) {
    const p = getLeavePeriod(leave.start_datetime, leave.end_datetime);
    return { isHalfM: p === 'morning', isHalfA: p === 'afternoon' };
  }
  if (dateStr === leaveStartDate) {
    const startHour = parseDateSafe(leave.start_datetime).getHours();
    return { isHalfM: false, isHalfA: startHour >= 12 };
  }
  if (dateStr === leaveEndDate) {
    const endHour = parseDateSafe(leave.end_datetime).getHours();
    return { isHalfM: endHour <= 12, isHalfA: false };
  }
  return { isHalfM: false, isHalfA: false };
}

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Calculate difference to get to Monday
  // If Sunday (0), go back 6 days to Monday of that week; if Monday (1), stay; etc.
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDateRange = (startDate) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  return `${startDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${endDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;
};

const formatDateRangeShort = (startDate) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  return `${startDate.getDate()}/${startDate.getMonth() + 1} - ${endDate.getDate()}/${endDate.getMonth() + 1}`;
};

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

export default function GestionShifts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isTeamLeaderOrAbove, isManager } = useAuth();
  const { refreshTrigger, triggerRefresh } = useNotifications();
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [leaveToDelete, setLeaveToDelete] = useState(null); // leave pending deletion (confirm modal)
  const [deletingLeave, setDeletingLeave] = useState(false);
  const [weekStart, setWeekStart] = useState(() => {
    // If coming back from VueJournee, restore the week that contains that day
    const stateDate = location.state?.date;
    return stateDate ? getStartOfWeek(new Date(stateDate)) : getStartOfWeek(new Date());
  });
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"
  const [useRealHours, setUseRealHours] = useState(() => {
    const saved = localStorage.getItem('useRealHours');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('useRealHours', useRealHours.toString());
  }, [useRealHours]);

  // Weather state (Open-Meteo)
  const [weatherData, setWeatherData] = useState({});
  const [weatherLocation, setWeatherLocation] = useState(getSavedLocation);
  const [weatherModalState, setWeatherModalState] = useState({
    show: false,
    dayWeather: null,
  });

  const handleOpenWeatherModal = (dayWeather) => {
    setWeatherModalState({
      show: true,
      dayWeather,
    });
  };

  // Room management state
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(() => {
    // Restore from localStorage
    const saved = localStorage.getItem('selectedRoomId');
    return saved ? parseInt(saved) : null;
  });
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: "", color: "#3B82F6" });
  const [editingRoom, setEditingRoom] = useState(null);

  // Shift action modal state
  const [shiftActionModal, setShiftActionModal] = useState({
    show: false,
    shift: null,
    mode: null, // "edit", "delete", "request"
  });

  const [newShift, setNewShift] = useState({
    user_id: "",
    start_datetime: "",
    end_datetime: "",
    room_id: "",
    break_duration: 0,
    comment: "",
  });

  // Edit shift state
  const [editShift, setEditShift] = useState({
    user_id: "",
    start_datetime: "",
    end_datetime: "",
    no_show: false,
    room_id: "",
    break_duration: 0,
    comment: "",
  });

  // Request modification state
  const [requestModification, setRequestModification] = useState({
    start_time: "",
    end_time: "",
    break_duration: 0,
  });

  // Duplicate week state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateData, setDuplicateData] = useState({
    targetWeekOffset: 1, // Number of weeks to add (1 = next week, -1 = previous week)
    selectedUserIds: [], // Empty = all users
  });
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  // Skipped shifts notifications state (persisted in localStorage)
  const [skippedShiftNotifs, setSkippedShiftNotifs] = useState(() => {
    try {
      const saved = localStorage.getItem('skippedShiftNotifs');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });
  const [showSkippedPanel, setShowSkippedPanel] = useState(false);
  // Notif à dismiss automatiquement après création d'un shift (assigner un shift non assigné)
  const [pendingAssignNotifId, setPendingAssignNotifId] = useState(null);

  // ── States pour l'onglet Absence ──
  const [modalTab, setModalTab] = useState('shift'); // 'shift' | 'absence'
  const [absenceForm, setAbsenceForm] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'paid_leave',
    period: 'full',       // 'full' | 'morning' | 'afternoon' for single-day
    start_full: true,     // for multi-day: first day starts full (morning)
    end_full: true,       // for multi-day: last day ends full (evening)
  });
  const [absenceLoading, setAbsenceLoading] = useState(false);

  // Leave types configuration
  const LEAVE_TYPES = [
    { value: 'paid_leave', label: 'Congés payés (CP)' },
    { value: 'sick_leave', label: 'Arrêt maladie' },
    { value: 'work_accident', label: 'Accident du travail (AT)' },
    { value: 'unjustified', label: 'Absence injustifiée' },
    { value: 'compensatory', label: 'Repos compensateur (HS)' },
  ];

  // No-Show filter state
  const [showNoShowOnly, setShowNoShowOnly] = useState(false);

  const isCompact = true;

  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const tableRef = useRef(null);
  const theadRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current || !theadRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      // Subtract 1px to compensate for rounding errors causing gap
      const offset = rect.top <= 0 ? Math.min(-rect.top, Math.max(0, rect.height - 80)) - 1 : 0;
      
      setIsHeaderSticky(rect.top <= 0);

      // Update header nodes directly inside render cycle for lag-free rendering
      const ths = theadRef.current.querySelectorAll('th');
      ths.forEach(cell => {
        cell.style.transform = `translateY(${offset}px)`;
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isMinimal = isCompact || isHeaderSticky;

  // Mobile view states
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    // Par défaut, sélectionner aujourd'hui
    const today = new Date();
    const dayOfWeek = today.getDay();
    // Convertir dimanche (0) en 6, lundi (1) en 0, etc.
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  });
  const [showOnlyMyShifts, setShowOnlyMyShifts] = useState(false);
  const [mobileViewMode, setMobileViewMode] = useState('daily'); // 'daily' ou 'weekly'
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Shift Painter (Mode Remplissage) states
  const [paintMode, setPaintMode] = useState(false);
  const [paintTemplate, setPaintTemplate] = useState({
    start_time: "09:00",
    end_time: "17:00",
    break_duration: 0,
    comment: "",
    room_id: "",
    tag_ids: []
  });
  const [showPaintConfigModal, setShowPaintConfigModal] = useState(false);
  const [paintingCell, setPaintingCell] = useState(null); // { userId, dayIndex }

  // Tag management state
  const [tags, setTags] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTag, setNewTag] = useState({ name: "", color: "#3B82F6" });
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [showTagLegend, setShowTagLegend] = useState(false);

  // User reordering state
  const [usersOrder, setUsersOrder] = useState(() => {
    const saved = localStorage.getItem('usersOrder');
    return saved ? JSON.parse(saved) : [];
  });
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [tempOrder, setTempOrder] = useState([]);

  // Events state
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ date: "", title: "" });
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);

  // Selection and Clipboard states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [copiedShifts, setCopiedShifts] = useState(() => {
    try {
      const saved = localStorage.getItem('copiedShifts');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [showClipboardBar, setShowClipboardBar] = useState(true);

  // Clear selected shifts when week changes
  useEffect(() => {
    setSelectedShiftIds([]);
  }, [weekStart]);

  // Compute sorted users
  const sortedUsers = useMemo(() => {
    // Filtrer les utilisateurs inactifs qui n'ont pas de shift ou congé cette semaine
    const activeUsersOrWithShifts = users.filter((u) => {
      if (u.is_active !== false) return true; // Actif par défaut ou explicitement

      // Sinon, on vérifie si l'utilisateur a au moins un shift ou congé approuvé cette semaine
      return shifts.some((shift) => shift.user_id === u.id) ||
        approvedLeaves.some((leave) => leave.user_id === u.id);
    });

    if (usersOrder.length === 0) return activeUsersOrWithShifts;

    return [...activeUsersOrWithShifts].sort((a, b) => {
      const indexA = usersOrder.indexOf(a.id);
      const indexB = usersOrder.indexOf(b.id);

      // If both are found in order array, sort by index
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      // If only A is found, A comes first
      if (indexA !== -1) return -1;

      // If only B is found, B comes first
      if (indexB !== -1) return 1;

      // If neither found, sort alphabetically
      return (a.full_name || a.email).localeCompare(b.full_name || b.email);
    });
  }, [users, usersOrder, shifts, approvedLeaves]);

  // Room color palette
  const roomColorPalette = [
    "#3B82F6", // Blue
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#EF4444", // Red
    "#14B8A6", // Teal
    "#F97316", // Orange
  ];

  // Color palette suggestions
  const colorPalette = [
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#EF4444", // Red
    "#F59E0B", // Amber
    "#10B981", // Emerald
    "#14B8A6", // Teal
    "#06B6D4", // Cyan
    "#6366F1", // Indigo
    "#A855F7", // Violet
    "#F97316", // Orange
    "#84CC16", // Lime
    "#22D3EE", // Sky
    "#F43F5E", // Rose
    "#64748B", // Slate
  ];

  const token = localStorage.getItem("token");
  const SHIFTS_API = `${API_URL}/shifts`;
  const USERS_API = `${API_URL}/users`;
  const REQUESTS_API = `${API_URL}/requests`;
  const TAGS_API = `${API_URL}/tags`;
  const ROOMS_API = `${API_URL}/rooms`;
  const DAY_OFFS_API = `${API_URL}/day-offs`;

  useEffect(() => {
    if (!user?.id) return; // Wait for user to be loaded

    fetchWeekData();
    fetchUsers();
    fetchRequests();
    fetchTags();
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, refreshTrigger, user?.id, isTeamLeaderOrAbove]);

  // Fetch weather data for the current week using Open-Meteo
  useEffect(() => {
    let isCancelled = false;
    const loadWeather = async () => {
      try {
        const start = new Date(weekStart);
        start.setHours(12, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);

        const startStr = formatDateLocal(start);
        const endStr = formatDateLocal(end);

        const result = await fetchWeekWeather(startStr, endStr, weatherLocation);
        if (!isCancelled && result && result.dailyMap) {
          setWeatherData(result.dailyMap);
        }
      } catch (err) {
        console.warn("Erreur chargement météo Open-Meteo:", err);
      }
    };

    loadWeather();
    return () => {
      isCancelled = true;
    };
  }, [weekStart, weatherLocation]);

  // Persist selectedRoomId to localStorage
  useEffect(() => {
    if (selectedRoomId !== null) {
      localStorage.setItem('selectedRoomId', selectedRoomId.toString());
    } else {
      localStorage.removeItem('selectedRoomId');
    }
  }, [selectedRoomId]);

  // Detect screen size and switch view mode
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode("cards");
        setPaintMode(false); // Turn off stamp mode on mobile
      } else {
        setViewMode("table");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check for openShiftEditId from location state after shifts are loaded
  useEffect(() => {
    if (shifts.length > 0 && location.state?.openShiftEditId) {
      const shiftToEdit = shifts.find(s => s.id === location.state.openShiftEditId);
      if (shiftToEdit) {
        setShiftActionModal({
          show: true,
          shift: shiftToEdit,
          mode: "edit"
        });
        setEditShift({
          user_id: shiftToEdit.user_id,
          start_datetime: shiftToEdit.start_datetime,
          end_datetime: shiftToEdit.end_datetime,
          start_time: shiftToEdit.start_time?.slice(11, 16),
          end_time: shiftToEdit.end_time?.slice(11, 16),
          no_show: shiftToEdit.no_show || false,
          room_id: shiftToEdit.room_id || "",
          break_duration: shiftToEdit.break_duration || 0,
          comment: shiftToEdit.comment || "",
        });
        setSelectedTagIds(shiftToEdit.tags?.map((t) => t.id) || []);
        
        // Clear the state so it doesn't reopen on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [shifts, location.state, navigate, location.pathname]);

  const fetchWeekData = async () => {
    setLoading(true);
    try {
      const startStr = formatDateLocal(weekStart);

      let url = `${API_URL}/calendar/week?week_start=${startStr}`;
      if (isTeamLeaderOrAbove && user.id) {
        // Managers might want to filter, but default to seeing all. 
        // If we wanted to filter specific user for manager view we could add &user_id=...
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();

        // Process Events from Calendar
        const calendarEvents = data.events || [];

        // 1. Shifts
        const shiftsData = calendarEvents
          .filter(e => e.type === 'shift')
          .map(s => ({
            ...s,
            start_time: s.start_datetime, // Map datetime to time for frontend compatibility
            end_time: s.end_datetime
          }));
        setShifts(shiftsData);

        // 2. Allocating Users from Shifts (if needed)
        // Extract unique users from shifts using the new user_name and user_email fields
        if (shiftsData.length > 0) {
          setUsers(prevUsers => {
            const userMap = new Map();
            prevUsers.forEach(u => userMap.set(u.id, u));
            shiftsData.forEach(shift => {
              if (shift.user_id && !userMap.has(shift.user_id)) {
                userMap.set(shift.user_id, {
                  id: shift.user_id,
                  email: shift.user_email || `User ${shift.user_id}`,
                  full_name: shift.user_name || null,
                });
              }
            });
            return Array.from(userMap.values());
          });
        }

        // 3. Leaves & Day Offs
        const leavesData = calendarEvents
          .filter(e => e.type === 'leave')
          .map(l => ({
            ...l,
            // Ensure status is present if needed by UI, though calendar usually returns approved only
            status: l.status || 'approved'
          }));

        const dayOffsData = calendarEvents
          .filter(e => e.type === 'day_off')
          .map(d => ({
            ...d,
            start_date: d.date,
            end_date: d.date,
            status: 'approved', // Treat as approved leave for visualization
            is_day_off: true // Marker if we want different styling later
          }));

        setApprovedLeaves([...leavesData, ...dayOffsData]);

        // 4. Generic Events
        const eventsData = calendarEvents.filter(e => e.type === 'event');
        setEvents(eventsData);

        setError(null);
      } else {
        console.error("Erreur lors de la récupération du calendrier");
        setError("Impossible de charger le planning");
      }
    } catch (err) {
      console.error(err);
      setError("Impossible de charger le planning");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    // Ne pas faire de requête si non-team-leader-or-above (évite 403)
    if (!isTeamLeaderOrAbove) {
      setUsers([]); // Sera rempli depuis les données de shifts
      return;
    }

    try {
      const res = await fetch(`${USERS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        console.error("Erreur lors de la récupération des utilisateurs");
        setUsers([]);
      }
    } catch (err) {
      console.error(err);
      setUsers([]);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${REQUESTS_API}/?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      } else {
        console.error("Erreur lors de la récupération des demandes");
        setError("Impossible de charger les demandes");
      }
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les demandes");
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${TAGS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setTags(data);
      } else {
        console.error("Erreur lors de la récupération des tags");
        setTags([]);
      }
    } catch (err) {
      console.error(err);
      setTags([]);
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await fetch(`${ROOMS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setRooms(data);
      } else {
        console.error("Erreur lors de la récupération des rooms");
        setRooms([]);
      }
    } catch (err) {
      console.error(err);
      setRooms([]);
    }
  };

  const createRoom = async (e) => {
    e.preventDefault();

    if (!newRoom.name.trim()) {
      alert("Veuillez entrer un nom pour la salle");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${ROOMS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newRoom.name,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création de la salle");
      }

      await fetchRooms();
      setNewRoom({ name: "", color: "#3B82F6" });
      alert("Salle créée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRoom = async (e) => {
    e.preventDefault();

    if (!editingRoom || !editingRoom.name.trim()) {
      alert("Veuillez entrer un nom pour la salle");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${ROOMS_API}/${editingRoom.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editingRoom.name,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la modification de la salle");
      }

      await fetchRooms();
      await fetchWeekData(); // Refresh shifts to update room names
      setEditingRoom(null);
      alert("Salle modifiée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette salle ? Les shifts associés ne seront pas supprimés mais n'auront plus de salle assignée.")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${ROOMS_API}/${roomId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression de la salle");
      }

      // If deleted room was selected, reset selection
      if (selectedRoomId === roomId) {
        setSelectedRoomId(null);
      }

      await fetchRooms();
      await fetchWeekData(); // Refresh shifts
      alert("Salle supprimée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (e) => {
    e.preventDefault();

    if (!newTag.name.trim()) {
      alert("Veuillez entrer un nom pour l'étiquette");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${TAGS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newTag.name,
          color: newTag.color,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création de l'étiquette");
      }

      await fetchTags();
      setNewTag({ name: "", color: "#3B82F6" });
      alert("Étiquette créée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateTag = async (e) => {
    e.preventDefault();

    if (!editingTag || !editingTag.name.trim()) {
      alert("Veuillez entrer un nom pour l'étiquette");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${TAGS_API}/${editingTag.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editingTag.name,
          color: editingTag.color,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la modification de l'étiquette");
      }

      await fetchTags();
      setEditingTag(null);
      alert("Étiquette modifiée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (tagId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette étiquette ? Elle sera retirée de tous les shifts.")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${TAGS_API}/${tagId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression de l'étiquette");
      }

      await fetchTags();
      await fetchWeekData(); // Refresh shifts to update tag display
      alert("Étiquette supprimée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== EVENTS MANAGEMENT =====


  const getEventsForDate = (dateStr) => {
    return events.filter(e => e.date === dateStr);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();

    if (!eventForm.date || !eventForm.title.trim()) {
      alert("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const method = editingEvent ? "PATCH" : "POST";
      const url = editingEvent
        ? `${API_URL}/events/${editingEvent.id}`
        : `${API_URL}/events/`;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          date: toCalendarDateKey(eventForm.date),
          title: eventForm.title,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        // Pydantic returns detail as an array; format it for readability
        const detail = Array.isArray(error.detail)
          ? error.detail.map(err => err.msg || JSON.stringify(err)).join('\n')
          : (error.detail || "Erreur lors de la sauvegarde de l'événement");
        throw new Error(detail);
      }

      await fetchWeekData();
      setShowEventModal(false);
      setEventForm({ date: "", title: "" });
      setEditingEvent(null);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleEditEvent = (event) => {
    setEventForm({ date: event.date, title: event.title });
    setEditingEvent(event);
    setShowEventModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/events/${eventId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression de l'événement");
      }

      await fetchWeekData();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== DAY OFFS MANAGEMENT =====

  const createDayOff = async () => {
    if (!newShift.user_id || !newShift.start_datetime) {
      alert("Veuillez sélectionner un employé et une date");
      return;
    }

    setLoading(true);
    try {
      // Extract date from start_datetime (YYYY-MM-DD)
      const dateStr = newShift.start_datetime.split('T')[0];

      const res = await fetch(`${DAY_OFFS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: parseInt(newShift.user_id),
          date: dateStr,
          reason: "Repos"
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création du jour de repos");
      }

      await fetchWeekData();
      setShowModal(false);
      setNewShift({
        user_id: "",
        start_datetime: "",
        end_datetime: "",
        room_id: "",
        break_duration: 0,
        comment: "",
      });

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteDayOff = async (dayOffId) => {
    if (!dayOffId) {
      alert("Erreur: ID du jour de repos manquant");
      console.error("dayOffId is undefined or null");
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce jour de repos ?")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${DAY_OFFS_API}/${dayOffId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression du jour de repos");
      }

      await fetchWeekData();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Opens the styled confirmation modal for a leave deletion (manager only)
  const requestDeleteLeave = (leave) => {
    if (!isManager || !leave?.id) return;
    setLeaveToDelete(leave);
  };

  const confirmDeleteLeave = async () => {
    const leave = leaveToDelete;
    if (!isManager || !leave?.id) return;

    setDeletingLeave(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/${leave.id}/cancel`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok && res.status !== 204) {
        let detail = "Erreur lors de la suppression du congé";
        try { detail = (await res.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }

      setLeaveToDelete(null);
      await fetchWeekData();
      triggerRefresh();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setDeletingLeave(false);
    }
  };

  // ===== SHIFT VALIDATION (MANAGERS ONLY) =====
  const handleValidateShifts = async (shiftIds) => {
    if (!isManager) {
      alert("Seuls les managers peuvent valider des shifts");
      return;
    }

    if (!shiftIds || shiftIds.length === 0) {
      alert("Aucun shift à valider");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SHIFTS_API}/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shift_ids: shiftIds }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la validation");
      }

      const result = await res.json();
      await fetchWeekData();

      alert(`${result.validated_count} shift(s) validé(s) avec succès`);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateSingleShift = (shiftId) => {
    if (window.confirm("Voulez-vous valider ce shift ?")) {
      handleValidateShifts([shiftId]);
    }
  };




  const handlePublishWeek = async () => {
    // Collecte tous les shifts non-publiés visibles dans la semaine
    const unpublishedShifts = shifts.filter(shift => !shift.is_published && !shift.no_show);

    if (unpublishedShifts.length === 0) {
      alert("Aucun shift en brouillon à publier pour cette semaine.");
      return;
    }

    if (!window.confirm(`Vous allez publier ${unpublishedShifts.length} shift(s). Les employés concernés recevront un email récapitulatif. Continuer ?`)) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shifts/publish`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shift_ids: unpublishedShifts.map(s => s.id) }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la publication des shifts");
      }

      const result = await res.json();
      await fetchWeekData();
      alert(`${result.published_count} shift(s) publié(s) avec succès.\n${result.notified_count} employé(s) notifié(s) par email.`);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateWeekShifts = () => {
    // Get all past shifts for this week
    const now = new Date();
    const pastShifts = shifts.filter(shift => {
      const shiftEnd = parseDateSafe(shift.end_time);
      return shiftEnd < now && !shift.validated;
    });

    if (pastShifts.length === 0) {
      const allPastShifts = shifts.filter(shift => {
        const shiftEnd = parseDateSafe(shift.end_time);
        return shiftEnd < now;
      });
      if (allPastShifts.length > 0) {
        if (window.confirm("Tous les shifts passés de cette semaine sont déjà validés. Souhaitez-vous forcer le recalcul et la synchronisation des heures supplémentaires pour cette semaine ?")) {
          handleValidateShifts(allPastShifts.map(s => s.id));
        }
        return;
      }
      alert("Aucun shift passé trouvé pour cette semaine");
      return;
    }

    if (window.confirm(`Voulez-vous valider ${pastShifts.length} shift(s) pour cette semaine ?`)) {
      handleValidateShifts(pastShifts.map(s => s.id));
    }
  };


  // Helper function to create a date in local timezone from YYYY-MM-DD string
  const createLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper to format date as YYYY-MM-DD using local time
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to check if a user is on leave on a specific date
  const isUserOnLeave = (userId, dateStr) => {
    return approvedLeaves.find(leave => {
      if (leave.user_id !== userId) return false;
      const startDate = toCalendarDateKey(leave.start_datetime || leave.start_date);
      const endDate   = toCalendarDateKey(leave.end_datetime || leave.end_date);
      return startDate <= dateStr && endDate >= dateStr;
    });
  };

  const handleWeekChange = (direction) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + direction * 7);
    setWeekStart(getStartOfWeek(newDate));
  };

  const resetToToday = () => {
    const today = new Date();

    // Check if we're in day view (path contains /jour/)
    if (location.pathname.includes('/gestionShifts/jour/')) {
      // Navigate to today's day view
      const todayStr = formatDateLocal(today);
      navigate(`/gestionShifts/jour/${todayStr}`);
    } else {
      // Navigate to today's week in week view
      setWeekStart(getStartOfWeek(today));
    }
  };

  // Helper: parse a skipped_shift string to extract user name and date
  const parseSkippedShift = (message) => {
    // Formats: "... for user John Doe on 2025-11-24" or "... for user John Doe: ..."
    const dateMatch = message.match(/on (\d{4}-\d{2}-\d{2})/);
    const nameMatch = message.match(/for user (.+?)(?:\s+on\s|:\s)/);
    return {
      date: dateMatch ? dateMatch[1] : null,
      userName: nameMatch ? nameMatch[1] : null,
    };
  };

  // Helper: save skipped notifs to state + localStorage
  const saveSkippedNotifs = (notifs) => {
    setSkippedShiftNotifs(notifs);
    localStorage.setItem('skippedShiftNotifs', JSON.stringify(notifs));
  };

  const dismissSkippedNotif = (id) => {
    const updated = skippedShiftNotifs.filter(n => n.id !== id);
    saveSkippedNotifs(updated);
  };

  const clearAllSkippedNotifs = () => {
    saveSkippedNotifs([]);
    setShowSkippedPanel(false);
  };

  const goToSkippedShiftDay = (dateStr) => {
    if (!dateStr) return;
    const targetDate = new Date(dateStr + 'T00:00:00');
    const targetWeekStart = getStartOfWeek(targetDate);
    setWeekStart(targetWeekStart);
    // Calculate day index (0=Monday, 6=Sunday)
    const dayOfWeek = targetDate.getDay();
    setSelectedDayIndex(dayOfWeek === 0 ? 6 : dayOfWeek - 1);
    setShowSkippedPanel(false);
  };

  const handleDuplicateWeek = async () => {
    if (!isTeamLeaderOrAbove) return;

    setDuplicateLoading(true);
    try {
      // Ensure we have the correct Monday of the current week
      // weekStart should already be Monday, but let's be explicit
      const sourceMonday = new Date(weekStart);
      sourceMonday.setHours(0, 0, 0, 0);

      // Calculate target Monday by adding the offset in weeks
      const targetMonday = new Date(sourceMonday);
      targetMonday.setDate(targetMonday.getDate() + (duplicateData.targetWeekOffset * 7));

      // Format dates as YYYY-MM-DD for backend using local timezone (not UTC)
      const sourceStartDate = formatDateLocal(sourceMonday);
      const targetStartDate = formatDateLocal(targetMonday);

      const payload = {
        source_start_date: sourceStartDate,
        target_start_date: targetStartDate,
        user_ids: duplicateData.selectedUserIds.length > 0 ? duplicateData.selectedUserIds : null,
      };

      const res = await fetch(`${SHIFTS_API}/duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la duplication");
      }

      const result = await res.json();

      await fetchWeekData();
      setShowDuplicateModal(false);
      setDuplicateData({ targetWeekOffset: 1, selectedUserIds: [] });

      // Save skipped shifts as persistent notifications
      if (result.skipped_shifts && result.skipped_shifts.length > 0) {
        const daysOffset = duplicateData.targetWeekOffset * 7;
        const newNotifs = result.skipped_shifts.map((msg, idx) => {
          const parsed = parseSkippedShift(msg);
          // Try to find the source shift (before duplication) by matching user name + source date
          let startTime = null;
          let endTime = null;
          let breakDuration = 0;
          let roomId = null;
          let roomName = null;
          let tagIds = [];
          if (parsed.date && parsed.userName) {
            // The target date is parsed.date; compute the source date by reversing the offset
            const targetDate = new Date(parsed.date + 'T00:00:00');
            const sourceDate = new Date(targetDate);
            sourceDate.setDate(sourceDate.getDate() - daysOffset);
            const sourceDateStr = formatDateLocal(sourceDate);
            // Match against loaded shifts by user name and source date
            const matchedShift = shifts.find(s => {
              const shiftDate = toCalendarDateKey(s.start_time || s.start_datetime);
              const shiftName = s.user_name || '';
              return shiftDate === sourceDateStr && shiftName === parsed.userName;
            });
            if (matchedShift) {
              startTime = matchedShift.start_time || matchedShift.start_datetime || null;
              endTime = matchedShift.end_time || matchedShift.end_datetime || null;
              breakDuration = matchedShift.break_duration || 0;
              roomId = matchedShift.room_id || null;
              roomName = matchedShift.room_name || null;
              tagIds = (matchedShift.tags || []).map(t => t.id);
            }
          }

          // Build a clear French message
          let frenchMessage = '';
          if (parsed.userName && parsed.date) {
            const dateObj = new Date(parsed.date + 'T00:00:00');
            const dateFr = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            const startFr = startTime ? formatTimeSafe(startTime, { hour: '2-digit', minute: '2-digit' }) : null;
            const endFr = endTime ? formatTimeSafe(endTime, { hour: '2-digit', minute: '2-digit' }) : null;
            frenchMessage = `Shift de ${parsed.userName} du ${dateFr}`
              + (startFr && endFr ? ` (${startFr} – ${endFr})` : '')
              + ` non dupliqué : un shift existe déjà ce jour-là.`;
          } else {
            frenchMessage = 'Un shift n\'a pas pu être dupliqué (conflit existant).';
          }

          return {
            id: `skip_${Date.now()}_${idx}`,
            message: frenchMessage,
            rawMessage: msg,
            date: parsed.date,
            userName: parsed.userName,
            startTime,
            endTime,
            breakDuration,
            roomId,
            roomName,
            tagIds,
            createdAt: new Date().toISOString(),
          };
        });
        const merged = [...skippedShiftNotifs, ...newNotifs];
        saveSkippedNotifs(merged);
      }

      alert(
        `Duplication terminée !\n` +
        `✅ ${result.created_count} shift(s) créé(s)\n` +
        `⚠️ ${result.skipped_count} shift(s) ignoré(s)` +
        (result.skipped_shifts.length > 0 ? `\n\nConsultez le détail via l'icône 🔔` : '')
      );
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setDuplicateLoading(false);
    }
  };

  // ── GESTION ABSENCE ──
  const handleCreateAbsence = async (e) => {
    e?.preventDefault();
    if (!absenceForm.user_id || !absenceForm.start_date || !absenceForm.end_date) return;

    setAbsenceLoading(true);
    try {
      const isSameDayAbs = absenceForm.start_date === absenceForm.end_date;
      const dtBoundsAbs = buildLeaveDatetimes(
        absenceForm.start_date, absenceForm.end_date,
        isSameDayAbs ? absenceForm.period : { startFull: absenceForm.start_full, endFull: absenceForm.end_full }
      );
      const payload = {
        user_id: parseInt(absenceForm.user_id),
        ...dtBoundsAbs,
        reason: absenceForm.reason || undefined,
        leave_type: absenceForm.leave_type,
      };
      console.log("ABSENCE PAYLOAD:", payload);

      const res = await fetch(`${API_URL}/api/leaves/request/employee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur lors de la création de l\'absence');
      }

      const responseData = await res.json();
      setShowModal(false);
      setModalTab('shift');
      setAbsenceForm({
        user_id: '', start_date: '', end_date: '',
        reason: '', leave_type: 'paid_leave',
        period: 'full', start_full: true, end_full: true,
      });
      fetchWeekData(); // Call fetchWeekData without arguments to refresh current week
      triggerRefresh();

      if (responseData.is_anticipated) {
        alert('Absence ajoutée et approuvée par anticipation (solde insuffisant) !');
      } else {
        alert('Absence ajoutée et approuvée avec succès.');
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setAbsenceLoading(false);
    }
  };


  const handleCreateShift = async (e) => {
    e.preventDefault();

    if (!newShift.user_id || !newShift.start_datetime || !newShift.end_datetime) {
      alert("Veuillez remplir tous les champs");
      return;
    }

    if (newShift.end_datetime <= newShift.start_datetime) {
      alert("La fin du shift doit être postérieure au début");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        user_id: parseInt(newShift.user_id),
        start_time: `${newShift.start_datetime}:00`,
        end_time: `${newShift.end_datetime}:00`,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        room_id: (newShift.room_id !== "" && newShift.room_id !== null) ? parseInt(newShift.room_id) : undefined,
        break_duration: parseInt(newShift.break_duration) || 0,
        comment: newShift.comment || "",
      };

      const res = await fetch(`${SHIFTS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création du shift");
      }

      const createdShift = await res.json();

      await fetchWeekData();
      setShowModal(false);
      setNewShift({
        user_id: "",
        start_datetime: "",
        end_datetime: "",
        room_id: "",
        break_duration: 0,
        comment: "",
      });
      setSelectedTagIds([]);
      setError(null);

      // Afficher les warnings s'il y en a (ex: non-respect des 11h de repos en mode souple)
      if (createdShift.warnings && createdShift.warnings.length > 0) {
        alert(`Shift créé avec succès mais avec les avertissements suivants:\n${createdShift.warnings.join('\n')}`);
      }

      // Si on vient d'assigner un shift non assigné, dismiss la notif correspondante
      if (pendingAssignNotifId) {
        dismissSkippedNotif(pendingAssignNotifId);
        setPendingAssignNotifId(null);
      }
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Paint a shift on a day cell using the pre-configured paintTemplate
  const handleDayCellPaint = async (userId, dayIndex) => {
    if (paintingCell) return; // Prevent concurrent requests

    const currentDate = new Date(weekStart);
    currentDate.setHours(12, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + dayIndex);
    const dateStr = formatDateLocal(currentDate);

    // Check if user is on leave — but allow half-day leaves (only block full-day)
    const leave = isUserOnLeave(userId, dateStr);
    if (leave && !leave.is_day_off) {
      let isHalfM = false;
      let isHalfA = false;
      if (leave.start_datetime) {
        const halves = getLeaveHalves(leave, dateStr);
        isHalfM = halves.isHalfM;
        isHalfA = halves.isHalfA;
      }
      const isFullLeave = !isHalfM && !isHalfA;
      if (isFullLeave) {
        const employeeName = users.find(u => u.id === userId)?.full_name ||
          users.find(u => u.id === userId)?.email ||
          'Cet employé';
        const leaveStartStr = toCalendarDateKey(leave.start_datetime || leave.start_date);
        const leaveEndStr   = toCalendarDateKey(leave.end_datetime || leave.end_date);
        const leaveStartDate = createLocalDate(leaveStartStr);
        const leaveEndDate   = createLocalDate(leaveEndStr);
        window.alert(
          `Impossible de créer un shift :\n${employeeName} est en congé du ${leaveStartDate.toLocaleDateString('fr-FR')} au ${leaveEndDate.toLocaleDateString('fr-FR')}`
        );
        return;
      }
    }

    setPaintingCell({ userId, dayIndex });
    try {
      const startDateStr = dateStr;
      let endDateStr = dateStr;

      // Handle overnight shift: if end_time <= start_time, end date is next day
      if (paintTemplate.end_time <= paintTemplate.start_time) {
        const nextDate = new Date(currentDate);
        nextDate.setDate(nextDate.getDate() + 1);
        endDateStr = formatDateLocal(nextDate);
      }

      const start_time = `${startDateStr}T${paintTemplate.start_time}:00`;
      const end_time = `${endDateStr}T${paintTemplate.end_time}:00`;

      const payload = {
        user_id: parseInt(userId),
        start_time,
        end_time,
        tag_ids: paintTemplate.tag_ids && paintTemplate.tag_ids.length > 0 ? paintTemplate.tag_ids : undefined,
        room_id: (paintTemplate.room_id !== "" && paintTemplate.room_id !== null) ? parseInt(paintTemplate.room_id) : undefined,
        break_duration: parseInt(paintTemplate.break_duration) || 0,
        comment: paintTemplate.comment || "",
      };

      const res = await fetch(`${SHIFTS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création du shift");
      }

      await fetchWeekData();
    } catch (err) {
      console.error(err);
      alert(err.message || "Une erreur est survenue lors de l'ajout du shift.");
    } finally {
      setPaintingCell(null);
    }
  };

  // Handle clicking on a day cell (manager only)
  const handleDayCellClick = (userId, dayIndex) => {
    if (!isTeamLeaderOrAbove) return;

    if (paintMode) {
      handleDayCellPaint(userId, dayIndex);
      return;
    }

    const currentDate = new Date(weekStart);
    currentDate.setHours(12, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + dayIndex);
    const dateStr = formatDateLocal(currentDate);

    // Check if user is on leave — but allow half-day leaves (only block full-day)
    const leave = isUserOnLeave(userId, dateStr);
    if (leave && !leave.is_day_off) {
      let isHalfM = false;
      let isHalfA = false;
      if (leave.start_datetime) {
        const halves = getLeaveHalves(leave, dateStr);
        isHalfM = halves.isHalfM;
        isHalfA = halves.isHalfA;
      }
      const isFullLeave = !isHalfM && !isHalfA;
      if (isFullLeave) {
        const employeeName = users.find(u => u.id === userId)?.full_name ||
          users.find(u => u.id === userId)?.email ||
          'Cet employé';
        const leaveStartStr = toCalendarDateKey(leave.start_datetime || leave.start_date);
        const leaveEndStr   = toCalendarDateKey(leave.end_datetime || leave.end_date);
        const leaveStartDate = createLocalDate(leaveStartStr);
        const leaveEndDate   = createLocalDate(leaveEndStr);
        window.alert(
          `Impossible de créer un shift :\n${employeeName} est en congé du ${leaveStartDate.toLocaleDateString('fr-FR')} au ${leaveEndDate.toLocaleDateString('fr-FR')}`
        );
        return;
      }
    }

    // Format datetime-local values (YYYY-MM-DDTHH:MM)
    const startDatetime = `${dateStr}T09:00`;
    const endDatetime = `${dateStr}T17:00`;

    setNewShift({
      user_id: userId.toString(),
      start_datetime: startDatetime,
      end_datetime: endDatetime,
      room_id: selectedRoomId !== null ? selectedRoomId.toString() : "",
    });
    setSelectedTagIds([]);
    setAbsenceForm(prev => ({
      ...prev,
      user_id: userId.toString(),
      start_date: dateStr,
      end_date: dateStr
    }));
    setShowModal(true);
  };


  // Handle clicking on an existing shift
  const handleShiftClick = (shift) => {
    // Safety check for shift times
    if (!shift?.start_time || !shift?.end_time) {
      console.error("Shift missing time data:", shift);
      return;
    }

    if (paintMode && isTeamLeaderOrAbove) {
      const shiftDate = parseDateSafe(shift.start_time);
      if (shiftDate) {
        const shiftWeekStart = getStartOfWeek(shiftDate);
        const dayIndex = getCalendarDayOffset(shiftDate, shiftWeekStart);
        handleDayCellPaint(shift.user_id, dayIndex);
      }
      return;
    }

    if (selectionMode && isTeamLeaderOrAbove) {
      handleToggleShiftSelection(shift.id);
      return;
    }

    const shiftUser = users.find(u => u.id === shift.user_id);
    const currentUserId = parseInt(localStorage.getItem("user_id"));
    const isOwnShift = shift.user_id === currentUserId;

    // Determine mode based on user role
    if (isTeamLeaderOrAbove) {
      // Team leader or manager can edit or delete any shift
      // Convert to datetime-local format (remove seconds)
      setEditShift({
        user_id: shift.user_id ? shift.user_id.toString() : "",
        start_datetime: shift.start_time.slice(0, 16),
        end_datetime: shift.end_time.slice(0, 16),
        no_show: shift.no_show || false, // Initialize no_show field
        room_id: shift.room_id !== null && shift.room_id !== undefined ? shift.room_id.toString() : "",
        break_duration: shift.break_duration || 0,
        comment: shift.comment || "",
      });
      // Initialize selected tags from shift, filtering out any null/undefined values
      setSelectedTagIds(shift.tags ? shift.tags.map(tag => tag.id).filter(id => id !== null && id !== undefined) : []);
      setShiftActionModal({
        show: true,
        shift: { ...shift, user: shiftUser },
        mode: "edit",
      });
    } else {
      // Non-manager can only interact with their own shifts
      if (!isOwnShift) {
        if (shift.comment) {
          setShiftActionModal({
            show: true,
            shift: { ...shift, user: shiftUser },
            mode: "view-comment",
          });
        }
        return;
      }

      // Check if there's a pending request for this shift
      const pendingRequest = getPendingRequestForShift(shift.id);

      if (pendingRequest) {
        // Show request details in view-only mode
        setShiftActionModal({
          show: true,
          shift: { ...shift, user: shiftUser },
          mode: "view-request",
          request: pendingRequest,
        });
      } else {
        // Regular user can request modification on their own shift
        setRequestModification({
          start_time: shift.start_time.slice(0, 16),
          end_time: shift.end_time.slice(0, 16),
          break_duration: parseInt(shift.break_duration) || 0,
        });
        setShiftActionModal({
          show: true,
          shift: { ...shift, user: shiftUser },
          mode: "request",
        });
      }
    }
  };

  // Handle updating a shift (manager only)
  const handleUpdateShift = async () => {
    const { shift } = shiftActionModal;

    if (editShift.end_datetime <= editShift.start_datetime) {
      alert("La fin du shift doit être postérieure au début");
      return;
    }

    setLoading(true);
    try {
      // Filter out any null/undefined values from tag_ids
      const validTagIds = selectedTagIds.filter(id => id !== null && id !== undefined);

      const payload = {
        start_time: editShift.start_datetime?.length === 16 ? `${editShift.start_datetime}:00` : editShift.start_datetime,
        end_time: editShift.end_datetime?.length === 16 ? `${editShift.end_datetime}:00` : editShift.end_datetime,
        user_id: editShift.user_id ? parseInt(editShift.user_id) : shift.user_id,
        tag_ids: validTagIds,
        no_show: editShift.no_show || false, // Add no_show field
        room_id: (editShift.room_id !== "" && editShift.room_id !== null) ? parseInt(editShift.room_id) : null,
        break_duration: parseInt(editShift.break_duration) || 0,
        comment: editShift.comment || "",
      };

      const res = await fetch(`${SHIFTS_API}/${shift.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        console.error("Backend error details:", error);
        console.error("Payload sent:", payload);
        throw new Error(JSON.stringify(error.detail || error));
      }

      const updatedShiftData = await res.json();

      await fetchWeekData();
      setShiftActionModal({ show: false, shift: null, mode: null });
      setError(null);

      // Afficher les warnings s'il y en a (ex: non-respect des 11h de repos en mode souple)
      if (updatedShiftData.warnings && updatedShiftData.warnings.length > 0) {
        alert(`Shift modifié avec succès mais avec les avertissements suivants:\n${updatedShiftData.warnings.join('\n')}`);
      }
    } catch (err) {
      console.error("Full error:", err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a shift (manager only)
  const handleDeleteShift = async () => {
    const { shift } = shiftActionModal;

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce shift ?")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SHIFTS_API}/${shift.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression du shift");
      }

      await fetchWeekData();
      setShiftActionModal({ show: false, shift: null, mode: null });
      setError(null);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle requesting modification (regular user)
  const handleRequestModification = async () => {
    const { shift } = shiftActionModal;

    if (requestModification.end_time <= requestModification.start_time) {
      alert("L'heure de fin doit être postérieure à l'heure de début");
      return;
    }

    const originalStart = shift.start_time.slice(0, 16);
    const originalEnd = shift.end_time.slice(0, 16);
    const originalBreak = parseInt(shift.break_duration) || 0;
    const reqBreak = parseInt(requestModification.break_duration) || 0;

    if (
      requestModification.start_time === originalStart &&
      requestModification.end_time === originalEnd &&
      reqBreak === originalBreak
    ) {
      alert("Aucune modification n'a été apportée au shift.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        shift_id: shift.id,
        start_time: `${requestModification.start_time}:00`,
        end_time: `${requestModification.end_time}:00`,
        break_duration: reqBreak,
      };

      const res = await fetch(`${REQUESTS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création de la demande");
      }

      await fetchRequests(); // Refresh requests to show the pending badge
      triggerRefresh(); // Update notification count in Layout
      alert("Demande de modification créée avec succès !");
      setShiftActionModal({ show: false, shift: null, mode: null });
      setError(null);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get pending request for a shift
  const getPendingRequestForShift = (shiftId) => {
    return requests.find(req => req.shift_id === shiftId && req.status === "pending");
  };

  // Handle approving a request (manager only)
  const handleApproveRequest = async () => {
    const { shift } = shiftActionModal;
    const pendingRequest = getPendingRequestForShift(shift.id);

    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/approve`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de l'approbation");
      }

      await fetchWeekData();
      await fetchRequests();
      triggerRefresh(); // Update notification count in Layout
      setShiftActionModal({ show: false, shift: null, mode: null });
      alert("Demande approuvée avec succès !");
      setError(null);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle modifying a shift from a request
  const handleModifyFromRequest = async () => {
    const { shift } = shiftActionModal;
    const pendingRequest = getPendingRequestForShift(shift.id);

    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/reject`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de l'annulation de la demande");
      }

      await fetchRequests();
      triggerRefresh();
      // Le composant va se re-render, la demande va disparaître et laisser place aux champs d'édition
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle rejecting a request (manager only)
  const handleRejectRequest = async () => {
    const { shift } = shiftActionModal;
    const pendingRequest = getPendingRequestForShift(shift.id);

    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/reject`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors du rejet");
      }

      await fetchRequests();
      triggerRefresh(); // Update notification count in Layout
      setShiftActionModal({ show: false, shift: null, mode: null });
      alert("Demande rejetée.");
      setError(null);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserColor = (userId) => {
    // Si users est vide (non-team-leader-or-above), utiliser userId directement
    if (users.length === 0) {
      return employeeColors[userId % employeeColors.length];
    }

    const index = users.findIndex(u => u.id === userId);
    if (index === -1) return employeeColors[userId % employeeColors.length]; // Fallback avec userId
    return employeeColors[index % employeeColors.length];
  };

  // Filter shifts by selected room (show selected room + unassigned shifts)
  const getFilteredShiftsByRoom = (shiftsArray) => {
    if (selectedRoomId === null || selectedRoomId === undefined) return shiftsArray; // Show all if no room selected
    return shiftsArray.filter(shift =>
      shift.room_id === selectedRoomId || shift.room_id === null || shift.room_id === undefined // Show selected room + unassigned
    );
  };

  // Get room color by index
  const getRoomColor = (roomId) => {
    const index = rooms.findIndex(r => r.id === roomId);
    if (index === -1) return roomColorPalette[0];
    return roomColorPalette[index % roomColorPalette.length];
  };

  // Calculate total hours for a room in the current week
  const calculateRoomHours = (roomId) => {
    const roomShifts = shifts.filter(shift => shift.room_id === roomId);
    let totalMinutes = 0;

    roomShifts.forEach(shift => {
      if (shift.start_time && shift.end_time) {
        const start = parseDateSafe(shift.start_time);
        const end = parseDateSafe(shift.end_time);
        const duration = (end - start) / (1000 * 60); // minutes
        const breakMinutes = shift.break_duration || 0;
        totalMinutes += Math.max(0, duration - breakMinutes);
      }
    });

    return totalMinutes / 60; // return hours
  };

  // Handle room selection change
  const handleRoomChange = (roomId) => {
    setSelectedRoomId(roomId);
  };

  // Reorder Modal Logic
  const handleOpenOrderModal = () => {
    setTempOrder(sortedUsers.map(u => u.id));
    setShowOrderModal(true);
  };

  const moveUser = (index, direction) => {
    const newOrder = [...tempOrder];
    if (direction === 'up' && index > 0) {
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    } else if (direction === 'down' && index < newOrder.length - 1) {
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    }
    setTempOrder(newOrder);
  };

  // Drag and Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setTempOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveOrder = () => {
    setUsersOrder(tempOrder);
    localStorage.setItem('usersOrder', JSON.stringify(tempOrder));
    setShowOrderModal(false);
  };


  // Calculate total hours worked for a user in the DISPLAYED week only
  // Matches backend logic: only considers actual shifts (excluding no_show) and subtracts break time. 
  // Leaves do not count towards worked / overtime hours based on backend rules.
  const calculateWeeklyHours = (userId) => {
    // Calculate week end date
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    // Filter shifts for this user in the displayed week only
    const userShifts = shifts.filter(shift => {
      if (shift.user_id !== userId) return false;

      const shiftDate = parseDateSafe(shift.start_time);
      return shiftDate >= weekStart && shiftDate < weekEnd;
    });

    let totalMinutes = 0;
    userShifts.forEach(shift => {
      if (shift.start_time && shift.end_time && !shift.no_show) {
        const start = parseDateSafe(shift.start_time);
        const end = parseDateSafe(shift.end_time);
        const duration = (end - start) / (1000 * 60); // minutes
        const breakMinutes = shift.break_duration || 0;
        totalMinutes += Math.max(0, duration - breakMinutes);
      }
    });

    return totalMinutes / 60; // return hours
  };

  // Count no-show shifts for a user in the DISPLAYED week only
  const calculateNoShowCount = (userId) => {
    // Calculate week end date
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    // Filter no-show shifts for this user in the displayed week only
    const userShifts = shifts.filter(shift => {
      if (shift.user_id !== userId) return false;
      if (!shift.no_show) return false;

      const shiftDate = parseDateSafe(shift.start_time);
      return shiftDate >= weekStart && shiftDate < weekEnd;
    });

    return userShifts.length;
  };

  // Calculate quota status for a user (worked hours vs quota)
  const calculateQuotaStatus = (userId) => {
    const employee = users.find(u => u.id === userId);
    if (!employee || !employee.weekly_hours) {
      return null;
    }

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    let overlapDays = 0;
    const userLeaves = approvedLeaves.filter(leave => {
      if (leave.user_id !== userId) return false;
      if (leave.is_day_off) return false;

      const startField = toCalendarDateKey(leave.start_datetime || leave.start_date);
      const endField   = toCalendarDateKey(leave.end_datetime || leave.end_date);
      const leaveStart = new Date(startField);
      const leaveEnd = new Date(endField);

      return leaveStart < weekEnd && leaveEnd >= weekStart;
    });

    userLeaves.forEach(leave => {
      // Force to local midnight to avoid timezone shift when parsing
      const startField = toCalendarDateKey(leave.start_datetime || leave.start_date);
      const endField   = toCalendarDateKey(leave.end_datetime || leave.end_date);
      
      const leaveStart = new Date(`${startField}T00:00:00`);
      const leaveEnd = new Date(`${endField}T00:00:00`);

      const weekStartDate = new Date(weekStart);
      weekStartDate.setHours(0, 0, 0, 0);
      
      const weekEndDate = new Date(weekStart);
      weekEndDate.setDate(weekEndDate.getDate() + 6);
      weekEndDate.setHours(0, 0, 0, 0);

      const overlapStart = leaveStart >= weekStartDate ? leaveStart : weekStartDate;
      const overlapEnd = leaveEnd <= weekEndDate ? leaveEnd : weekEndDate;

      if (overlapStart <= overlapEnd) {
        const daysDiff = getCalendarDayOffset(overlapEnd, overlapStart) + 1;
        overlapDays += daysDiff;
      }
    });

    const quotaHours = parseFloat(employee.weekly_hours);
    const workDays = employee.work_days ? employee.work_days.length : 5;
    const weightPerDay = quotaHours / workDays;
    const valuedAbsence = overlapDays * weightPerDay;

    const workedHours = calculateWeeklyHours(userId);
    const totalContribution = workedHours + valuedAbsence;

    // Overtime = only hours physically worked above the contract (CP cannot generate overtime)
    const overtimeHours = Math.max(0, workedHours - quotaHours);
    // Deficit = contract hours not covered by shifts + valued absences combined
    const missingHours = Math.max(0, quotaHours - totalContribution);

    return {
      worked: workedHours,
      totalContribution: totalContribution,
      quota: quotaHours,
      percentage: (totalContribution / quotaHours) * 100,
      status: totalContribution >= quotaHours ? 'exceeded' : 'under',
      missingHours: missingHours,
      overtimeHours: overtimeHours,
    };
  };

  // Apply no-show filter to shifts array
  const applyNoShowFilter = (shiftsArray) => {
    if (!showNoShowOnly) return shiftsArray;
    return shiftsArray.filter(shift => shift.no_show);
  };

  // Get shifts for a specific user and day (with room filtering and no-show filter)
  const getShiftsForUserAndDay = (userId, dayIndex) => {
    const currentDate = new Date(weekStart);
    currentDate.setHours(12, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + dayIndex);
    const dayShifts = shifts.filter(
      (shift) => shift.user_id === userId && isSameDay(parseDateSafe(shift.start_time), currentDate)
    );
    const roomFiltered = getFilteredShiftsByRoom(dayShifts);
    return applyNoShowFilter(roomFiltered);
  };

  // Get all shifts for the week grouped by day (with room filtering and no-show filter)
  const getShiftsGroupedByDay = () => {
    const grouped = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(weekStart);
      currentDate.setHours(12, 0, 0, 0);
      currentDate.setDate(currentDate.getDate() + i);
      const dayShifts = shifts.filter((shift) =>
        isSameDay(parseDateSafe(shift.start_time), currentDate)
      );
      const roomFiltered = getFilteredShiftsByRoom(dayShifts);
      const finalFiltered = applyNoShowFilter(roomFiltered);
      grouped.push({
        date: currentDate,
        dayName: days[i],
        dayShort: daysShort[i],
        shifts: finalFiltered,
      });
    }
    return grouped;
  };

  // Filter shifts based on user preference (mobile view)
  const getFilteredShifts = (dayShifts) => {
    if (!showOnlyMyShifts) return dayShifts;

    const currentUserId = parseInt(localStorage.getItem("user_id"));
    return dayShifts.filter(shift => shift.user_id === currentUserId);
  };

  const today = new Date();

  // ── SELECTION AND COPY-PASTE HELPERS ──
  const getVisibleShiftsForDay = (dayIndex) => {
    let visibleShifts = [];
    users.forEach(employee => {
      const dayShifts = getShiftsForUserAndDay(employee.id, dayIndex);
      visibleShifts = [...visibleShifts, ...dayShifts];
    });
    return visibleShifts;
  };

  const handleToggleShiftSelection = (shiftId) => {
    setSelectedShiftIds(prev =>
      prev.includes(shiftId)
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const handleToggleDaySelection = (dayIndex) => {
    const dayShifts = getVisibleShiftsForDay(dayIndex);
    if (dayShifts.length === 0) return;

    const dayShiftIds = dayShifts.map(s => s.id);
    const allSelected = dayShiftIds.every(id => selectedShiftIds.includes(id));

    if (allSelected) {
      setSelectedShiftIds(prev => prev.filter(id => !dayShiftIds.includes(id)));
    } else {
      setSelectedShiftIds(prev => {
        const next = [...prev];
        dayShiftIds.forEach(id => {
          if (!next.includes(id)) next.push(id);
        });
        return next;
      });
    }
  };

  const handleValidateSelected = async () => {
    if (selectedShiftIds.length === 0) return;
    if (window.confirm(`Voulez-vous valider les ${selectedShiftIds.length} shift(s) sélectionné(s) ?`)) {
      await handleValidateShifts(selectedShiftIds);
      setSelectedShiftIds([]);
      setSelectionMode(false);
    }
  };

  const handleCopySelected = () => {
    if (selectedShiftIds.length === 0) return;

    const shiftsToCopy = shifts.filter(s => selectedShiftIds.includes(s.id));

    const serialized = [];
    for (const shift of shiftsToCopy) {
      const shiftDate = parseDateSafe(shift.start_time);
      const shiftEndDate = parseDateSafe(shift.end_time);

      if (!shiftDate || !shiftEndDate) {
        console.warn("Skipping copy for shift due to invalid date:", shift);
        continue;
      }

      const shiftWeekStart = getStartOfWeek(shiftDate);
      const startOffsetDays = getCalendarDayOffset(shiftDate, shiftWeekStart);
      const endOffsetDays = getCalendarDayOffset(shiftEndDate, shiftWeekStart);

      const getTimePart = (dateStr, dateObj) => {
        if (typeof dateStr === 'string' && dateStr.length >= 16) {
          const char10 = dateStr.charAt(10);
          if (char10 === 'T' || char10 === ' ') {
            return dateStr.slice(11, 16);
          }
        }
        const h = String(dateObj.getHours()).padStart(2, '0');
        const m = String(dateObj.getMinutes()).padStart(2, '0');
        return `${h}:${m}`;
      };

      const timePartStart = getTimePart(shift.start_time, shiftDate);
      const timePartEnd = getTimePart(shift.end_time, shiftEndDate);

      serialized.push({
        user_id: shift.user_id,
        startOffsetDays,
        endOffsetDays,
        timePartStart,
        timePartEnd,
        room_id: shift.room_id,
        break_duration: shift.break_duration,
        comment: shift.comment,
        tag_ids: shift.tags ? shift.tags.map(t => t.id).filter(id => id !== null && id !== undefined) : []
      });
    }

    setCopiedShifts(serialized);
    localStorage.setItem('copiedShifts', JSON.stringify(serialized));
    setShowClipboardBar(true);
    alert(`${serialized.length} shift(s) copié(s) ! Naviguez vers une autre semaine et cliquez sur "Coller sur cette semaine"`);

    setSelectionMode(false);
  };

  const handleClearClipboard = () => {
    setCopiedShifts([]);
    localStorage.removeItem('copiedShifts');
  };

  const handlePasteShifts = async () => {
    if (!copiedShifts || copiedShifts.length === 0) return;

    if (!window.confirm(`Voulez-vous coller les ${copiedShifts.length} shift(s) sur cette semaine (${formatDateRangeShort(weekStart)}) ?`)) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    let warningMessages = [];

    try {
      for (const shift of copiedShifts) {
        if (shift.startOffsetDays === null || shift.startOffsetDays === undefined ||
            shift.endOffsetDays === null || shift.endOffsetDays === undefined ||
            !shift.timePartStart || !shift.timePartEnd) {
          console.warn("Skipping paste for invalid copied shift data:", shift);
          errorCount++;
          continue;
        }

        const targetStart = new Date(weekStart);
        targetStart.setDate(targetStart.getDate() + shift.startOffsetDays);
        const startDateStr = formatDateLocal(targetStart);
        const start_time = `${startDateStr}T${shift.timePartStart}:00`;

        const targetEnd = new Date(weekStart);
        targetEnd.setDate(targetEnd.getDate() + shift.endOffsetDays);
        const endDateStr = formatDateLocal(targetEnd);
        const end_time = `${endDateStr}T${shift.timePartEnd}:00`;

        const payload = {
          user_id: shift.user_id,
          start_time,
          end_time,
          room_id: shift.room_id || undefined,
          break_duration: shift.break_duration,
          comment: shift.comment || "",
          tag_ids: shift.tag_ids && shift.tag_ids.length > 0 ? shift.tag_ids : undefined
        };

        try {
          const res = await fetch(`${SHIFTS_API}/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          if (res.ok) {
            successCount++;
            const createdShift = await res.json();
            if (createdShift.warnings && createdShift.warnings.length > 0) {
              const userName = users.find(u => u.id === shift.user_id)?.full_name || `Employé #${shift.user_id}`;
              warningMessages.push(`${userName} (${startDateStr}): ${createdShift.warnings.join(', ')}`);
            }
          } else {
            errorCount++;
          }
        } catch (e) {
          errorCount++;
        }
      }

      await fetchWeekData();

      let msg = `${successCount} shift(s) collé(s) avec succès.`;
      if (errorCount > 0) {
        msg += `\n${errorCount} shift(s) n'ont pas pu être collés.`;
      }
      if (warningMessages.length > 0) {
        msg += `\n\nAvertissements :\n` + warningMessages.join('\n');
      }
      alert(msg);
    } catch (err) {
      console.error(err);
      alert("Une erreur est survenue lors du collage des shifts.");
    } finally {
      setLoading(false);
    }
  };

  // Navigate to day view
  const handleDayHeaderClick = (dayIndex) => {
    // Seuls les team leaders ou managers peuvent accéder à la vue journée
    if (!isTeamLeaderOrAbove) return;

    if (selectionMode) {
      handleToggleDaySelection(dayIndex);
      return;
    }

    const currentDate = new Date(weekStart);
    currentDate.setHours(12, 0, 0, 0);
    currentDate.setDate(currentDate.getDate() + dayIndex);
    const dateStr = formatDateLocal(currentDate);
    navigate(`/gestionShifts/jour/${dateStr}`);
  };

  // ── COMPOSANT RÉUTILISABLE : SWITCHES ½ JOURNÉE (MATIN / APRÈS-MIDI) ──
  const HalfDaySwitches = ({ form, setForm, disabledSameDay = false }) => {
    const isSameDayForm = disabledSameDay && form.start_date && form.end_date && form.start_date === form.end_date;

    if (isSameDayForm) {
      const period = form.period || 'full';

      return (
        <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <label className="text-xs sm:text-sm font-medium text-gray-700">Durée du congé</label>
          <div className="flex bg-white rounded-lg border border-gray-200 p-1">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, period: 'full' }))}
              className={`flex-1 px-1 sm:px-2 py-1.5 sm:py-2 text-[11px] sm:text-sm font-medium rounded-md transition-all ${period === 'full' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >Journée entière</button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, period: 'morning' }))}
              className={`flex-1 px-1 sm:px-2 py-1.5 sm:py-2 text-[11px] sm:text-sm font-medium rounded-md transition-all ${period === 'morning' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >Matinée</button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, period: 'afternoon' }))}
              className={`flex-1 px-1 sm:px-2 py-1.5 sm:py-2 text-[11px] sm:text-sm font-medium rounded-md transition-all ${period === 'afternoon' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >Après-midi</button>
          </div>
        </div>
      );
    }

    const startFull = form.start_full !== false;
    const endFull = form.end_full !== false;

    return (
      <div className="flex flex-col gap-3">
        {/* Ligne Début */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setForm(f => ({
                ...f,
                start_full: !f.start_full,
              }))}
            >
              <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out ${!startFull ? 'bg-indigo-600' : ''}`}>
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${!startFull ? 'translate-x-4' : ''}`}></div>
              </div>
              <div className="text-sm font-medium text-gray-700 select-none">½ journée au début</div>
            </div>
          </div>
        </div>

        {/* Ligne Fin */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setForm(f => ({ ...f, end_full: !f.end_full }))}
            >
              <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out ${!endFull ? 'bg-indigo-600' : ''}`}>
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${!endFull ? 'translate-x-4' : ''}`}></div>
              </div>
              <div className="text-sm font-medium text-gray-700 select-none">½ journée à la fin</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800">
      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-3 sm:py-8">
        {/* Header - Responsive */}
        <div className="mb-3 sm:mb-6">
          {/* Mobile Layout (< 640px) - Stacked layout with arrows next to title */}
          <div className="flex flex-col gap-2 sm:hidden">
            {/* Title */}
            <h1 className="text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Gestion des Shifts
            </h1>

            {/* Navigation with date in center */}
            <div className="flex items-center justify-between bg-white rounded-xl shadow-md p-2">
              <button
                onClick={() => handleWeekChange(-1)}
                className="hover:bg-gray-100 p-2 rounded-lg transition-colors touch-manipulation"
                aria-label="Semaine précédente"
              >
                <ChevronLeft className="w-5 h-5 text-indigo-600" />
              </button>

              {/* Date range in center */}
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-1.5 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span key={weekStart.getTime()} className="text-lg font-bold">{formatDateRangeShort(weekStart)}</span>
                </div>
              </div>

              <button
                onClick={() => handleWeekChange(1)}
                className="hover:bg-gray-100 p-2 rounded-lg transition-colors touch-manipulation"
                aria-label="Semaine suivante"
              >
                <ChevronRight className="w-5 h-5 text-indigo-600" />
              </button>
            </div>

            {/* Mobile Actions Row */}
            <div className="flex items-center gap-1.5">
              {/* Skipped Shifts Bell (Mobile) */}
              {isTeamLeaderOrAbove && skippedShiftNotifs.length > 0 && (
                <button
                  onClick={() => setShowSkippedPanel(!showSkippedPanel)}
                  className="relative p-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 active:scale-95 transition-all touch-manipulation"
                >
                  <Bell className="w-5 h-5 text-amber-500" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {skippedShiftNotifs.length}
                  </span>
                </button>
              )}
              <button
                onClick={resetToToday}
                className="flex items-center justify-center gap-1 flex-1 text-xs px-2 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg font-semibold transition-all duration-200 touch-manipulation shadow-md hover:shadow-lg active:scale-95"
              >
                <Calendar className="w-3.5 h-3.5" />
                Aujourd'hui
              </button>
              {selectedRoomId && rooms.find(r => r.id === selectedRoomId) && (
                <div className="flex items-center justify-center px-2 py-2 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold truncate max-w-[100px] shadow-sm">
                  {rooms.find(r => r.id === selectedRoomId).name}
                </div>
              )}

              {isTeamLeaderOrAbove && (
                <button
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                  }}
                  className={`flex items-center justify-center p-2 border rounded-lg shadow-sm active:scale-95 transition-all duration-200 touch-manipulation ${
                    selectionMode
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Sélection multiple"
                >
                  <Check className="w-5 h-5" />
                </button>
              )}

              {/* Unified Actions Menu (Mobile) */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="flex items-center gap-2 px-2 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 active:scale-95 transition-all duration-200 font-medium touch-manipulation"
                >
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>

                {showActionsMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowActionsMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
                      {/* 1. New Shift (Manager Only) */}
                      {isTeamLeaderOrAbove && (
                        <div className="px-2 pb-2 border-b border-gray-100">
                          <button
                            onClick={() => {
                              setNewShift(prev => ({ ...prev, room_id: selectedRoomId !== null ? selectedRoomId.toString() : "" }));
                              setShowModal(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors font-medium text-sm"
                          >
                            <div className="p-1.5 bg-indigo-200 rounded-md">
                              <Plus className="w-3.5 h-3.5" />
                            </div>
                            Nouveau Shift
                          </button>
                        </div>
                      )}

                      {/* 2. Filters & Views */}
                      <div className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50 mt-1">
                        Affichage
                      </div>

                      {/* Room Filter */}
                      {rooms.length > 0 && (
                        <div className="px-2 py-1">
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <select
                              value={selectedRoomId !== null ? selectedRoomId : ""}
                              onChange={(e) => handleRoomChange(e.target.value === "" ? null : parseInt(e.target.value))}
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white hover:bg-gray-50 transition-colors cursor-pointer appearance-none"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="">Toutes les salles</option>
                              {rooms.map((room) => (
                                <option key={room.id} value={room.id}>{room.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Toggle Options */}
                      <button
                        onClick={() => setShowNoShowOnly(!showNoShowOnly)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${showNoShowOnly ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-700'}`}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>Absences</span>
                        </div>
                        {showNoShowOnly && <Check className="w-3.5 h-3.5" />}
                      </button>

                      {/* Hours Format Toggle */}
                      <button
                        onClick={() => setUseRealHours(!useRealHours)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${useRealHours ? 'text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100/50' : 'text-gray-700'}`}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-indigo-600" />
                          <span>{useRealHours ? "Heures réelles (ex: 1h30)" : "Heures décimales (ex: 1.5h)"}</span>
                        </div>
                        {useRealHours && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                      </button>

                      {tags.length > 0 && (
                        <button
                          onClick={() => setShowTagLegend(!showTagLegend)} // Reusing existing state logic for legend visibility if needed, or I can just use a local toggle? Wait, showTagLegend is likely used in desktop too. 
                          // Actually mobile had specific toggle. Desktop shows it always? No desktop was line 1698.
                          // Let's use showTagLegend state to toggle visibility of legend block on page? 
                          // Or I can add a legend INSIDE the menu?
                          // Let's keep the button to toggle the legend ON THE PAGE for now, or use the menu.
                          // The mobile legend was a dropdown.
                          // I'll make this toggle the legend overlay? 
                          // Simplification: Just show "Gérer les étiquettes" is enough for manager. For viewing?
                          // I'll skip "Toggle Legend" for now to keep it simple, or add "Afficher Légende" if the user wants it.
                          // User said "Fais la même logique...".
                          // Let's stick to No-Show toggle.
                          className="hidden" // Placeholder
                        />
                      )}

                      {/* 3. Management (Manager Only) */}
                      {isTeamLeaderOrAbove && (
                        <>
                          <div className="my-1 border-t border-gray-100"></div>
                          <div className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50">
                            Gestion
                          </div>

                          <button
                            onClick={() => { handleOpenOrderModal(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors text-sm"
                          >
                            <ListOrdered className="w-4 h-4 text-gray-500" />
                            Organiser l'équipe
                          </button>

                          <button
                            onClick={() => {
                              // Use today's date for mobile menu
                              const today = new Date();
                              const dateStr = formatDateLocal(today);
                              setEventForm({ date: dateStr, title: '' });
                              setEditingEvent(null);
                              setShowEventModal(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors text-sm"
                          >
                            <Calendar className="w-4 h-4 text-gray-500" />
                            Ajouter un événement
                          </button>

                          <button
                            onClick={() => { setShowDuplicateModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors text-sm"
                          >
                            <Copy className="w-4 h-4 text-gray-500" />
                            Dupliquer la semaine
                          </button>

                          <button
                            onClick={() => { setShowTagModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors text-sm"
                          >
                            <Tag className="w-4 h-4 text-gray-500" />
                            Gérer les étiquettes
                          </button>

                          <button
                            onClick={() => { handlePublishWeek(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-blue-700 transition-colors border-t border-gray-100 font-medium text-sm"
                          >
                            <Send className="w-4 h-4 text-blue-600" />
                            Publier la semaine
                          </button>

                          <button
                            onClick={() => { handleValidateWeekShifts(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 text-green-700 transition-colors border-t border-gray-100 text-sm"
                          >
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Valider la semaine
                          </button>

                          <button
                            onClick={() => { setShowRoomModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors text-sm"
                          >
                            <Building className="w-4 h-4 text-gray-500" />
                            Gérer les salles
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tablet/Desktop Layout (>= 640px) */}
          <div className="hidden sm:flex sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-lg font-medium bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Gestion des Shifts
              </h1>
              <p className="text-3xl font-bold text-gray-500 mt-1 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span key={weekStart.getTime()}>{formatDateRange(weekStart)}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Active Room Filter Badge */}
              {selectedRoomId && rooms.find(r => r.id === selectedRoomId) && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-700 shadow-sm animate-in fade-in zoom-in-95 duration-200">
                  <Building className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm font-semibold truncate max-w-[150px]">
                    {rooms.find(r => r.id === selectedRoomId).name}
                  </span>
                </div>
              )}

              {/* Week Navigation */}
              <div className="flex items-center space-x-2 text-gray-500 bg-white rounded-lg shadow-sm px-2 py-1">
                <button
                  onClick={() => handleWeekChange(-1)}
                  className="hover:bg-gray-100 p-2 rounded transition-colors touch-manipulation"
                  aria-label="Semaine précédente"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={resetToToday}
                  className="text-sm px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors touch-manipulation"
                >
                  Aujourd'hui
                </button>
                <button
                  onClick={() => handleWeekChange(1)}
                  className="hover:bg-gray-100 p-2 rounded transition-colors touch-manipulation"
                  aria-label="Semaine suivante"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Skipped Shifts Bell (Desktop) */}
              {isTeamLeaderOrAbove && skippedShiftNotifs.length > 0 && (
                <button
                  onClick={() => setShowSkippedPanel(!showSkippedPanel)}
                  className="relative p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 active:scale-95 transition-all border border-gray-200"
                >
                  <Bell className="w-5 h-5 text-amber-500" />
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {skippedShiftNotifs.length}
                  </span>
                </button>
              )}

              {isTeamLeaderOrAbove && (
                <button
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg shadow-sm font-medium transition-all active:scale-95 duration-200 touch-manipulation ${
                    selectionMode
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  <span>Sélection multiple</span>
                </button>
              )}

              {/* Unified Actions Menu - Replacing all individual buttons */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200 font-medium touch-manipulation"
                >
                  <Settings className="w-5 h-5" />
                  <span>Actions</span>
                </button>

                {showActionsMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowActionsMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
                      {/* 1. New Shift (High Priority) - Manager Only */}
                      {isTeamLeaderOrAbove && (
                        <div className="px-2 pb-2 border-b border-gray-100 space-y-1.5">
                          <button
                            onClick={() => {
                              setNewShift(prev => ({ ...prev, room_id: selectedRoomId !== null ? selectedRoomId.toString() : "" }));
                              setShowModal(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors font-medium"
                          >
                            <div className="p-1.5 bg-indigo-200 rounded-md">
                              <Plus className="w-4 h-4" />
                            </div>
                            Nouveau Shift
                          </button>
                          <button
                            onClick={() => {
                              setShowPaintConfigModal(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors font-medium"
                          >
                            <div className="p-1.5 bg-purple-200 rounded-md">
                              <Paintbrush className="w-4 h-4" />
                            </div>
                            Mode Remplissage (Tampon)
                          </button>
                        </div>
                      )}

                      {/* 2. Filters & Views */}
                      <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50 mt-1">
                        Affichage
                      </div>

                      {/* Room Filter */}
                      {rooms.length > 0 && (
                        <div className="px-2 py-1">
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <select
                              value={selectedRoomId !== null ? selectedRoomId : ""}
                              onChange={(e) => handleRoomChange(e.target.value === "" ? null : parseInt(e.target.value))}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white hover:bg-gray-50 transition-colors cursor-pointer appearance-none"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="">Toutes les salles</option>
                              {rooms.map((room) => (
                                <option key={room.id} value={room.id}>{room.name}</option>
                              ))}
                            </select>
                            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                          </div>
                        </div>
                      )}

                      {/* No-Show Filter Toggle */}
                      <button
                        onClick={() => setShowNoShowOnly(!showNoShowOnly)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${showNoShowOnly ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-700'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1 rounded-md ${showNoShowOnly ? 'bg-red-200 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                            <AlertCircle className="w-3.5 h-3.5" />
                          </div>
                          <span>Afficher Absences</span>
                        </div>
                        {showNoShowOnly && <Check className="w-4 h-4" />}
                      </button>

                      {/* Hours Format Toggle */}
                      <button
                        onClick={() => setUseRealHours(!useRealHours)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${useRealHours ? 'text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100/50' : 'text-gray-700'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1 rounded-md ${useRealHours ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                            <Clock className="w-3.5 h-3.5" />
                          </div>
                          <span>{useRealHours ? "Heures réelles (ex: 1h30)" : "Heures décimales (ex: 1.5h)"}</span>
                        </div>
                        {useRealHours && <Check className="w-4 h-4 text-indigo-600" />}
                      </button>


                      {/* 3. Management (Manager Only) */}
                      {isTeamLeaderOrAbove && (
                        <>
                          <div className="my-1 border-t border-gray-100"></div>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50">
                            Gestion
                          </div>

                          <button
                            onClick={() => { handleOpenOrderModal(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                          >
                            <ListOrdered className="w-4 h-4 text-gray-500" />
                            Organiser l'équipe
                          </button>

                          <button
                            onClick={() => {
                              const today = new Date();
                              const dateStr = formatDateLocal(today);
                              setEventForm({ date: dateStr, title: '' });
                              setEditingEvent(null);
                              setShowEventModal(true);
                              setShowActionsMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                          >
                            <Calendar className="w-4 h-4 text-gray-500" />
                            Ajouter un événement
                          </button>

                          <button
                            onClick={() => { setShowDuplicateModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                          >
                            <Copy className="w-4 h-4 text-gray-500" />
                            Dupliquer la semaine
                          </button>

                          <button
                            onClick={() => { setShowTagModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                          >
                            <Tag className="w-4 h-4 text-gray-500" />
                            Gérer les étiquettes
                          </button>

                          <button
                            onClick={() => { handlePublishWeek(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-blue-700 transition-colors border-t border-gray-100 font-medium"
                          >
                            <Send className="w-4 h-4 text-blue-600" />
                            Publier la semaine
                          </button>

                          <button
                            onClick={() => { handleValidateWeekShifts(); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 text-green-700 transition-colors border-t border-gray-100"
                          >
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Valider la semaine
                          </button>

                          <button
                            onClick={() => { setShowRoomModal(true); setShowActionsMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-gray-700 transition-colors"
                          >
                            <Building className="w-4 h-4 text-gray-500" />
                            Gérer les salles
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>



        {/* Tag Legend - Hidden on mobile (< 640px), visible on tablet/desktop */}
        {tags.length > 0 && (
          <div className="hidden sm:block mb-3 p-1.5 bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">Étiquettes:</span>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 border border-gray-200"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span style={{ color: tag.color }}>{tag.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 text-sm">
            <span className="font-medium">⚠️ {error}</span>
          </div>
        )}

        {/* Desktop Table View */}
        {viewMode === "table" && (
          <div ref={tableRef} className="overflow-x-auto border border-gray-200 rounded-2xl bg-white shadow-xl">
            <table className="w-full border-collapse">
              <thead ref={theadRef} className="relative z-40">
                <tr className="border-b border-gray-200">
                  <th
                    className={`text-left font-medium text-gray-600 sticky left-0 bg-gray-50 border-b border-gray-200 shadow-sm ${isMinimal ? 'p-1.5 min-w-[130px]' : 'p-3 min-w-[180px]'}`}
                    style={{ zIndex: 30 }}
                  >
                    <div className="flex items-center gap-1.5">
                      <Users className={`${isMinimal ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                      <span className={`${isMinimal ? 'text-xs' : 'text-sm'}`}>Employé</span>
                    </div>
                  </th>
                  {days.map((day, i) => {
                    const currentDate = new Date(weekStart);
                    currentDate.setHours(12, 0, 0, 0);
                    currentDate.setDate(currentDate.getDate() + i);
                    const isToday = isSameDay(currentDate, today);
                    const dateStr = formatDateLocal(currentDate);

                    return (
                      <th
                        key={day}
                        className={`text-center font-medium border-b border-gray-200 shadow-sm ${isMinimal ? 'p-1 min-w-[115px]' : 'p-2 min-w-[140px]'} ${isToday
                          ? "bg-indigo-100 text-indigo-700"
                          : "text-gray-600 bg-gray-50"
                          }`}
                        style={{ zIndex: 90 }}
                      >
                        <div
                          onClick={() => handleDayHeaderClick(i)}
                          className={`${isTeamLeaderOrAbove ? 'cursor-pointer hover:bg-opacity-80' : ''}`}
                          title={isTeamLeaderOrAbove ? (selectionMode ? 'Sélectionner/Désélectionner tous les shifts de cette journée' : `Voir la vue détaillée du ${day} ${currentDate.getDate()}/${currentDate.getMonth() + 1}`) : `${day} ${currentDate.getDate()}/${currentDate.getMonth() + 1}`}
                        >
                          {selectionMode && isTeamLeaderOrAbove && (
                            <div className="flex justify-center mb-1">
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const dayShifts = getVisibleShiftsForDay(i);
                                  return dayShifts.length > 0 && dayShifts.every(s => selectedShiftIds.includes(s.id));
                                })()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleDaySelection(i);
                                }}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          )}
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            <span className={`font-semibold ${isMinimal ? 'text-xs' : 'text-sm'}`}>{isMinimal ? daysShort[i] : day}</span>
                            {weatherData[dateStr] && (
                              <WeatherBadge
                                dayWeather={weatherData[dateStr]}
                                isMinimal={isMinimal}
                                onClick={handleOpenWeatherModal}
                              />
                            )}
                          </div>
                          <div className={`${isMinimal ? 'text-[10px] mt-0' : 'text-xs mt-0.5'} font-normal text-gray-500`}>
                            {currentDate.getDate()}/{currentDate.getMonth() + 1}
                          </div>
                        </div>

                        {/* Events Badge */}
                        {(() => {
                          const dayEvents = getEventsForDate(dateStr);

                          if (dayEvents.length === 0) return null;

                          return (
                            <div className="mt-2 space-y-1 max-h-16 max-w-[140px] overflow-y-auto scrollbar-thin scrollbar-thumb-amber-300 scrollbar-track-amber-50">
                              {dayEvents.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={() => setViewingEvent(event)}
                                  className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between group hover:bg-amber-100 transition-colors cursor-pointer"
                                  title="Cliquez pour voir les détails"
                                >
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    <span className="text-amber-600 text-xs flex-shrink-0"></span>
                                    <span className="text-[10px] font-medium text-amber-800 truncate leading-tight">
                                      {event.title}
                                    </span>
                                  </div>

                                  {isTeamLeaderOrAbove && (
                                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex-shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditEvent(event);
                                        }}
                                        className="p-0.5 hover:bg-amber-200 rounded"
                                        title="Modifier"
                                      >
                                        <Edit2 className="w-2.5 h-2.5 text-amber-600" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteEvent(event.id);
                                        }}
                                        className="p-0.5 hover:bg-red-100 rounded"
                                        title="Supprimer"
                                      >
                                        <Trash2 className="w-2.5 h-2.5 text-red-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* ── Ligne des shifts non dupliqués (team leader ou manager) ── */}
                {isTeamLeaderOrAbove && (() => {
                  // Filtrer les notifs qui tombent dans la semaine affichée
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekEnd.getDate() + 7);
                  const notifsThisWeek = skippedShiftNotifs.filter(n => {
                    if (!n.date) return false;
                    const d = new Date(n.date + 'T00:00:00');
                    return d >= weekStart && d < weekEnd;
                  });
                  if (notifsThisWeek.length === 0) return null;

                  return (
                    <tr className="border-b-2 border-orange-300 bg-orange-50/60">
                      {/* Colonne label */}
                      <td className="p-2 sticky left-0 z-10 bg-orange-50 border-r border-orange-200 min-w-[180px]">
                        <div className="flex items-center gap-1.5">
                          <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                          <div>
                            <div className="text-xs font-bold text-orange-700 leading-tight">Non assignés</div>
                            <div className="text-[10px] text-orange-500 leading-tight">Shifts non dupliqués</div>
                          </div>
                        </div>
                      </td>
                      {/* Colonnes jours */}
                      {days.map((day, dayIndex) => {
                        const currentDate = new Date(weekStart);
                        currentDate.setHours(12, 0, 0, 0);
                        currentDate.setDate(currentDate.getDate() + dayIndex);
                        const dateStr = formatDateLocal(currentDate);
                        const dayNotifs = notifsThisWeek.filter(n => n.date === dateStr);
                        // Combiner la date CIBLE (notif.date) avec l'heure SOURCE (slice 11-16)
                        // pour corriger le décalage de date lors de la duplication
                        const toLocalDT = (isoStr, targetDate) =>
                          (isoStr && targetDate) ? `${targetDate}T${isoStr.slice(11, 16)}` : (isoStr ? isoStr.slice(0, 16) : '');

                        return (
                          <td key={day} className="p-1.5 align-top border-r border-orange-100 last:border-r-0">
                            {dayNotifs.length === 0 ? (
                              <div className="text-gray-200 text-xs text-center py-1">—</div>
                            ) : (
                              <div className="space-y-1">
                                {dayNotifs.map(notif => {
                                  const startStr = notif.startTime
                                    ? formatTimeSafe(notif.startTime, { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                  const endStr = notif.endTime
                                    ? formatTimeSafe(notif.endTime, { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                  return (
                                    <div
                                      key={notif.id}
                                      className="border-2 border-orange-400 bg-white rounded-lg p-1.5 shadow-sm"
                                    >
                                      <div className="text-[10px] font-bold text-orange-800 truncate leading-tight">
                                        {notif.userName || 'Inconnu'}
                                      </div>
                                      {startStr && endStr && (
                                        <div className="text-[10px] text-orange-600 font-semibold leading-tight">
                                          {startStr} – {endStr}
                                        </div>
                                      )}
                                      {notif.roomName && (
                                        <div className="text-[9px] text-gray-500 flex items-center gap-0.5 mt-0.5">
                                          <Building className="w-2.5 h-2.5 flex-shrink-0" />
                                          <span className="truncate">{notif.roomName}</span>
                                        </div>
                                      )}
                                      <div className="flex gap-1 mt-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            let endDateStr = notif.date;
                                            if (notif.startTime && notif.endTime && notif.date) {
                                              const sd = parseDateSafe(notif.startTime);
                                              const ed = parseDateSafe(notif.endTime);
                                              const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
                                              const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
                                              const dayDiff = Math.abs(getCalendarDayOffset(endDay, startDay));
                                              if (dayDiff > 0) {
                                                const targetEndObj = new Date(notif.date);
                                                targetEndObj.setDate(targetEndObj.getDate() + dayDiff);
                                                const y = targetEndObj.getFullYear();
                                                const m = String(targetEndObj.getMonth() + 1).padStart(2, '0');
                                                const d = String(targetEndObj.getDate()).padStart(2, '0');
                                                endDateStr = `${y}-${m}-${d}`;
                                              }
                                            }
                                            setPendingAssignNotifId(notif.id);
                                            setNewShift({
                                              user_id: '',
                                              start_datetime: toLocalDT(notif.startTime, notif.date),
                                              end_datetime: toLocalDT(notif.endTime, endDateStr),
                                              room_id: notif.roomId ? notif.roomId.toString() : '',
                                              break_duration: notif.breakDuration || 0,
                                            });
                                            setSelectedTagIds(notif.tagIds || []);
                                            setShowModal(true);
                                          }}
                                          className="flex-1 flex items-center justify-center gap-0.5 px-1 py-0.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[9px] font-bold transition-colors"
                                          title="Assigner ce shift à un employé"
                                        >
                                          <Plus className="w-2.5 h-2.5" />
                                          Assigner
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            dismissSkippedNotif(notif.id);
                                          }}
                                          className="flex items-center justify-center px-1 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 rounded text-[9px] transition-colors"
                                          title="Ignorer ce shift"
                                        >
                                          <Trash2 className="w-2.5 h-2.5" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })()}
                {sortedUsers.map((employee) => {
                  const colors = getUserColor(employee.id);
                  const isCurrentUser = employee.id === parseInt(localStorage.getItem("user_id"));

                  return (
                    <tr
                      key={employee.id}
                      className={`border-b transition-colors ${isCurrentUser
                        ? 'bg-indigo-50/50 border-indigo-200'
                        : 'border-gray-100'
                        }`}
                    >
                      <td className={`sticky left-0 z-10 border-r transition-all ${isCompact ? 'p-2' : 'p-4'} ${isCurrentUser ? 'bg-indigo-50/50 border-indigo-200' : 'bg-white border-gray-200'
                        }`}>
                        <div className={`flex items-center ${isCompact ? 'gap-2' : 'gap-3'}`}>
                          <div className={`rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white font-semibold shadow-md transition-all ${isCompact ? 'w-8 h-8 text-xs' : 'w-10 h-10'} ${isCurrentUser ? 'ring-2 ring-indigo-400' : ''
                            }`}>
                            {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-gray-800 truncate ${isCompact ? 'text-sm' : ''}`}>
                              {employee.full_name || employee.email}
                            </div>
                            {employee.role === 'MANAGER' && (
                              <div className={`${isCompact ? 'text-[10px]' : 'text-xs'} text-gray-500`}>Manager</div>
                            )}
                            {/* Weekly Hours Counter */}
                            {(() => {
                              const quotaStatus = calculateQuotaStatus(employee.id);
                              const noShowCount = calculateNoShowCount(employee.id);

                              const formatHours = (val) => {
                                return useRealHours ? formatDecimalHours(val) : `${parseFloat(val.toFixed(2))}h`;
                              };

                              // If no quota defined, show simple hours
                              if (!quotaStatus) {
                                return (
                                  <div className={`${isCompact ? 'mt-0.5' : 'mt-2'} space-y-0.5`}>
                                    <div className="flex items-center gap-1.5 text-xs">
                                      <Clock className="w-3 h-3 text-indigo-600" />
                                      <span className="font-semibold text-indigo-700">
                                        {formatHours(calculateWeeklyHours(employee.id))}
                                      </span>
                                      <span className="text-gray-500">cette semaine</span>
                                    </div>
                                    {noShowCount > 0 && (
                                      <div className="flex items-center gap-1.5 text-xs text-red-600">
                                        <X className="w-3 h-3" />
                                        <span className="font-semibold">
                                          {noShowCount} absence{noShowCount > 1 ? 's' : ''}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // With quota defined
                              return (
                                <div className={`${isCompact ? 'mt-0.5' : 'mt-2'} space-y-0.5`}>
                                  {/* Worked hours / Quota */}
                                  <div className="flex items-center gap-1 text-xs w-full">
                                    <Clock className="w-3 h-3 text-indigo-600 flex-shrink-0" />
                                    <span className="font-semibold text-indigo-700">
                                      {formatHours(quotaStatus.worked)}
                                    </span>
                                    <span className="text-gray-400">/</span>
                                    <span className="text-gray-600">{formatHours(quotaStatus.quota)}</span>
                                    {employee.contract_type !== 'CDI CADRE NO HS' && (
                                      quotaStatus.status === 'under' ? (
                                        <span className="font-bold text-orange-600 ml-auto flex-shrink-0">
                                          -{formatHours(quotaStatus.missingHours)}
                                        </span>
                                      ) : quotaStatus.overtimeHours > 0.01 ? (
                                        <span className="font-bold text-green-600 ml-auto flex-shrink-0">
                                          +{formatHours(quotaStatus.overtimeHours)}
                                        </span>
                                      ) : null
                                    )}
                                  </div>

                                  {/* No-show count */}
                                  {noShowCount > 0 && (
                                    <div className="flex items-center gap-1.5 text-xs text-red-600">
                                      <X className="w-3 h-3" />
                                      <span className="font-semibold">
                                        {noShowCount} absence{noShowCount > 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  )}

                                  {employee.contract_type === 'CDI CADRE NO HS' && (
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                      <span className="font-medium">Forfait jours</span>
                                    </div>
                                  )}

                                  {/* Progress bar */}
                                  <div className={`mt-1 w-full bg-gray-200 rounded-full ${isCompact ? 'h-1' : 'h-1.5'}`}>
                                    <div
                                      className={`rounded-full transition-all ${isCompact ? 'h-1' : 'h-1.5'} ${quotaStatus.percentage >= 100
                                        ? 'bg-green-500'
                                        : quotaStatus.percentage >= 75
                                          ? 'bg-indigo-500'
                                          : quotaStatus.percentage >= 50
                                            ? 'bg-orange-400'
                                            : 'bg-red-400'
                                        }`}
                                      style={{ width: `${Math.min(quotaStatus.percentage, 100)}%` }}
                                    />
                                  </div>

                                  {/* Percentage and contract type */}
                                  {/* <div className="text-[10px] text-gray-400">
                                    {quotaStatus.percentage.toFixed(0)}% du quota ({employee.contract_type})
                                  </div> */}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                      {days.map((day, dayIndex) => {
                        const currentDate = new Date(weekStart);
                        currentDate.setHours(12, 0, 0, 0);
                        currentDate.setDate(currentDate.getDate() + dayIndex);
                        const dateStr = formatDateLocal(currentDate);
                        const isToday = isSameDay(currentDate, today);
                        const dayShifts = getShiftsForUserAndDay(employee.id, dayIndex);
                        const onLeave = isUserOnLeave(employee.id, dateStr);

                        let isHalfM = false;
                        let isHalfA = false;
                        if (onLeave && !onLeave.is_day_off) {
                          if (onLeave.start_datetime) {
                            const halves = getLeaveHalves(onLeave, dateStr);
                            isHalfM = halves.isHalfM;
                            isHalfA = halves.isHalfA;
                          }
                        }

                        const isFullLeave = onLeave && !isHalfM && !isHalfA;

                        // Colored Block config per leave type
                        const leaveCfgMap = {
                          paid_leave:   { label: 'Congés payés',    abbr: 'CP',      blockBg: 'bg-indigo-50', border: 'border-indigo-400', text: 'text-indigo-700', tdBg: 'bg-indigo-50/50' },
                          sick_leave:   { label: 'Arrêt maladie',   abbr: 'Maladie', blockBg: 'bg-amber-50',  border: 'border-amber-400',  text: 'text-amber-700',  tdBg: 'bg-amber-50/40'  },
                          work_accident:{ label: 'Acc. travail',    abbr: 'AT',      blockBg: 'bg-red-50',    border: 'border-red-400',    text: 'text-red-700',    tdBg: 'bg-red-50/30'    },
                          unjustified:  { label: 'Abs. injustifiée',abbr: 'ABS',     blockBg: 'bg-gray-100',  border: 'border-gray-400',   text: 'text-gray-600',   tdBg: 'bg-gray-100/60'  },
                          compensatory: { label: 'Repos comp.',     abbr: 'RC',      blockBg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', tdBg: 'bg-purple-50/40' },
                        };
                        const leaveCfg = (onLeave && !onLeave.is_day_off)
                          ? (leaveCfgMap[onLeave.leave_type] || { label: 'Congé', abbr: 'Cong.', blockBg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', tdBg: 'bg-orange-50/40' })
                          : null;

                        const isPaintingThisCell = paintingCell && paintingCell.userId === employee.id && paintingCell.dayIndex === dayIndex;

                        return (
                          <td
                            key={day}
                            onClick={() => (!isFullLeave) && handleDayCellClick(employee.id, dayIndex)}
                            className={`text-center align-top transition-all relative group ${isCompact ? 'p-1' : 'p-2'} ${isToday ? "bg-indigo-50/30" : ""
                              } ${onLeave ? (onLeave.is_day_off ? "bg-gray-50/70 cursor-default" : `${leaveCfg?.tdBg ?? 'bg-orange-50/40'} cursor-default`) : ""
                              } ${!isFullLeave && isTeamLeaderOrAbove
                                ? (paintMode
                                    ? "cursor-copy hover:bg-purple-50 hover:ring-2 hover:ring-inset hover:ring-purple-300 hover:z-20"
                                    : "cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-inset hover:ring-blue-300 hover:z-20"
                                  )
                                : ""
                              }`}
                          >
                            {/* Paint Mode Preview on Hover */}
                            {!isFullLeave && paintMode && isTeamLeaderOrAbove && !isPaintingThisCell && (
                              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity bg-purple-100/90 border-2 border-dashed border-purple-500 rounded-lg m-1 shadow-inner">
                                <div className="text-[10px] font-bold text-purple-800">
                                  Peindre
                                </div>
                                <div className="text-[9px] font-semibold text-purple-700">
                                  {paintTemplate.start_time} - {paintTemplate.end_time}
                                </div>
                              </div>
                            )}

                            {/* Painting loading overlay */}
                            {isPaintingThisCell && (
                              <div className="absolute inset-0 bg-purple-50/80 flex items-center justify-center z-30 rounded-lg">
                                <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            {onLeave && (
                              onLeave.is_day_off ? (
                                <div
                                  className={`w-full rounded-lg border-l-4 border-gray-300 bg-gray-100 px-2 py-1.5 ${isFullLeave ? '' : 'mb-1'} ${isTeamLeaderOrAbove ? 'cursor-pointer hover:bg-red-50 hover:border-red-300 transition-colors group' : ''}`}
                                  onClick={(e) => {
                                    if (isTeamLeaderOrAbove) {
                                      e.stopPropagation();
                                      deleteDayOff(onLeave.id);
                                    }
                                  }}
                                  title={isTeamLeaderOrAbove ? "Cliquer pour supprimer ce jour de repos" : "Jour de repos"}
                                >
                                  <div className="flex items-center gap-1 text-xs font-semibold text-gray-600 group-hover:text-red-600 transition-colors">
                                    <Coffee className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{isCompact ? 'Repos' : 'Jour de repos'}</span>
                                  </div>
                                </div>
                              ) : (() => {
                                const cfg = leaveCfg;
                                return (
                                  <div
                                    className={`w-full rounded-lg border-l-4 ${cfg.blockBg} ${cfg.border} px-2 py-1.5 ${isFullLeave ? '' : 'mb-1'} ${isManager ? 'cursor-pointer hover:bg-red-50 hover:border-red-300 transition-colors group/leave' : ''}`}
                                    onClick={(e) => {
                                      if (isManager) {
                                        e.stopPropagation();
                                        requestDeleteLeave(onLeave);
                                      }
                                    }}
                                    title={isManager ? "Cliquer pour supprimer ce congé" : (onLeave.reason || cfg.label)}
                                  >
                                    <div className={`flex items-center gap-1 text-xs font-semibold ${cfg.text} ${isManager ? 'group-hover/leave:text-red-600 transition-colors' : ''}`}>
                                      <Plane className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{isCompact ? cfg.abbr : cfg.label}</span>
                                    </div>
                                    {(isHalfM || isHalfA) && (
                                      <span className={`text-[10px] font-medium ${cfg.text} opacity-70`}>
                                        {isHalfM ? 'Matin' : 'Après-midi'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()
                            )}

                            {(!isFullLeave || dayShifts.length > 0) && (
                              <div className="space-y-1">
                                {dayShifts.map((shift, idx) => {
                                  // Skip shifts with missing time data
                                  if (!shift?.start_time || !shift?.end_time) {
                                    console.warn("Skipping shift with missing time data:", shift);
                                    return null;
                                  }

                                  const start = parseDateSafe(shift.start_time);
                                  const end = parseDateSafe(shift.end_time);
                                  const duration = Math.round((end - start) / (1000 * 60));
                                  const breakDur = shift.break_duration || 0;
                                  const netDuration = Math.max(0, duration - breakDur);
                                  const hours = Math.floor(netDuration / 60);
                                  const minutes = netDuration % 60;
                                  const hasPendingRequest = getPendingRequestForShift(shift.id);
                                  const currentUserId = parseInt(localStorage.getItem("user_id"));
                                  const isOwnShift = shift.user_id === currentUserId;
                                  const isInteractive = isTeamLeaderOrAbove || isOwnShift || !!shift.comment;
                                  const primaryTag = shift.tags && shift.tags.length > 0 ? shift.tags[0] : null;
                                  const additionalTags = shift.tags && shift.tags.length > 1 ? shift.tags.slice(1) : [];
                                  const isUnassigned = shift.room_id === null || shift.room_id === undefined; // Check if shift has no room
                                  const isNoShow = shift.no_show; // Check if shift is no-show

                                  return (
                                    <div
                                      key={idx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShiftClick(shift);
                                      }}
                                      className={`relative rounded-lg shadow-sm transition-all ${isCompact ? 'p-1 border' : 'p-2 border-2'} ${isInteractive
                                        ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1'
                                        : 'cursor-default'
                                        } ${isUnassigned ? 'opacity-60' : ''} ${isNoShow ? 'opacity-50' : ''} ${selectedShiftIds.includes(shift.id) ? 'ring-2 ring-indigo-600 shadow-md ring-offset-1' : ''}`}
                                      style={isNoShow ? {
                                        backgroundColor: '#FEE2E2',
                                        borderColor: '#EF4444',
                                      } : (primaryTag ? {
                                        backgroundColor: `${primaryTag.color}15`,
                                        borderColor: primaryTag.color,
                                      } : {
                                        backgroundColor: isUnassigned ? '#F9FAFB' : '#F3F4F6',
                                        borderColor: isUnassigned ? '#D1D5DB' : '#9CA3AF'
                                      })}
                                    >
                                      {selectionMode && isTeamLeaderOrAbove && (
                                        <div className="absolute top-1 right-1 z-30 bg-white/90 rounded p-0.5 shadow-sm flex items-center justify-center">
                                          <input
                                            type="checkbox"
                                            checked={selectedShiftIds.includes(shift.id)}
                                            onChange={() => {}}
                                            className="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                            readOnly
                                          />
                                        </div>
                                      )}

                                      {hasPendingRequest && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white" title="Demande en attente" />
                                      )}

                                      {/* Validated Badge */}
                                      {shift.validated && !isNoShow && (
                                        <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-md z-10" title="Validé">
                                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                          </svg>
                                        </div>
                                      )}

                                      {/* Draft Badge */}
                                      {!shift.is_published && !isNoShow && isTeamLeaderOrAbove && (
                                        <div className="absolute -top-2 left-2 px-1 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 text-[8px] font-bold rounded shadow-sm z-10 flex items-center gap-0.5" title="Shift en brouillon (non publié)">
                                          🔒 BROUILLON
                                        </div>
                                      )}

                                      {/* No-Show Badge */}
                                      {isNoShow && (
                                        <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-600 text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-0.5" title="Absence non justifiée">
                                          <X className="w-2.5 h-2.5" />
                                          ABSENT
                                        </div>
                                      )}

                                      {/* Additional tag color indicators */}
                                      {additionalTags.length > 0 && (
                                        <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                                          {additionalTags.map((tag) => (
                                            <div
                                              key={tag.id}
                                              className="w-2 h-2 rounded-full border border-white shadow-sm flex-shrink-0"
                                              style={{ backgroundColor: tag.color }}
                                              title={tag.name}
                                            />
                                          ))}
                                        </div>
                                      )}

                                      <div className={`flex flex-col ${isCompact ? 'gap-0' : 'gap-0.5'}`}>
                                        <div className={`flex items-center justify-between gap-1 ${isCompact ? 'text-[10px]' : 'text-xs'} font-semibold ${isNoShow ? 'line-through text-red-600' : ''}`} style={!isNoShow && primaryTag ? { color: primaryTag.color } : { color: isNoShow ? '#DC2626' : '#6B7280' }}>
                                          <span>{shift.start_time?.slice(11, 16) || '??:??'}-{shift.end_time?.slice(11, 16) || '??:??'}</span>
                                          <span className={`font-normal ${isCompact ? 'text-[9px]' : 'text-[10px]'} ${isNoShow ? 'text-red-500 line-through' : 'text-gray-500 flex items-center gap-1'}`}>
                                            <span>{hours}h{minutes > 0 ? minutes : ''}</span>
                                            {!isNoShow && shift.break_duration > 0 && (
                                              <span className="text-orange-500 font-semibold">
                                                ({shift.break_duration}m)
                                              </span>
                                            )}
                                          </span>
                                        </div>

                                        {isNoShow && (
                                          <div className="text-[9px] text-red-600 font-semibold flex items-center gap-0.5">
                                            ⚠️ Non comptabilisé
                                          </div>
                                        )}

                                        {/* Room and Comment display */}
                                        {(!isNoShow && (shift.room_name || isUnassigned || shift.comment)) && (
                                          <div className="flex items-center justify-between gap-1">
                                            <div className={`text-[8.5px] text-gray-400 truncate ${isUnassigned && !shift.room_name ? 'italic' : ''}`}>
                                              {shift.room_name || 'Non assigné'}
                                            </div>
                                            {shift.comment && (
                                              <div className="text-blue-600 flex-shrink-0" title={shift.comment}>
                                                <MessageSquare className="w-2.5 h-2.5" />
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Bouton + pour ajouter un shift (toujours affiché) */}
                                <div className="group relative py-0.5 cursor-pointer hover:bg-indigo-100 rounded transition-colors mt-0.5 flex justify-center items-center">
                                  {/* Dash par défaut (toujours affiché) */}
                                  <div className="text-gray-300 text-[10px] group-hover:opacity-0 transition-opacity">
                                    —
                                  </div>
                                  {/* Icône + au survol (hidden in paintMode) */}
                                  {!paintMode && (
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Plus className="w-3.5 h-3.5 text-indigo-600" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                Aucun employé trouvé
              </div>
            )}
          </div>
        )}

        {/* Mobile Card View */}
        {viewMode === "cards" && (
          <div className="space-y-3">
            {/* Toggle entre vue journalière et vue hebdomadaire */}
            <div className="flex gap-2">
              <button
                onClick={() => setMobileViewMode('daily')}
                className={`flex-1 py-2 px-3 rounded-xl font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${mobileViewMode === 'daily'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow-md'
                  }`}
              >
                <Calendar className="w-4 h-4" />
                Par jour
              </button>
              <button
                onClick={() => setMobileViewMode('weekly')}
                className={`flex-1 py-2 px-3 rounded-xl font-medium transition-all flex items-center justify-center gap-1.5 text-sm ${mobileViewMode === 'weekly'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow-md'
                  }`}
              >
                <LayoutList className="w-4 h-4" />
                Ma semaine
              </button>
            </div>

            {mobileViewMode === 'weekly' ? (
              // Vue hebdomadaire personnelle
              <div className="space-y-3">
                {getShiftsGroupedByDay().map((day, dayIndex) => {
                  const currentUserId = parseInt(localStorage.getItem("user_id"));
                  const myShifts = (day.shifts || []).filter(s => s.user_id === currentUserId).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                  const myLeaves = (day.leaves || []).filter(l => l.user_id === currentUserId);
                  const isToday = isSameDay(day.date, today);

                  return (
                    <div
                      key={day.date.toISOString()}
                      className={`bg-white rounded-xl shadow-md border-2 p-3 ${isToday ? 'border-indigo-500 bg-indigo-50/30' : 'border-gray-200'
                        }`}
                    >
                      {/* En-tête du jour */}
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-bold text-base ${isToday ? 'text-indigo-600' : 'text-gray-800'}`}>
                              {days[dayIndex]}
                            </h3>
                            {weatherData[formatDateLocal(day.date)] && (
                              <WeatherBadge
                                dayWeather={weatherData[formatDateLocal(day.date)]}
                                onClick={handleOpenWeatherModal}
                              />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {day.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </p>
                        </div>
                        {isToday && (
                          <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            Aujourd'hui
                          </span>
                        )}
                      </div>

                      {/* Events Badge */}
                      {(() => {
                        const dateStr = formatDateLocal(day.date);
                        const dayEvents = getEventsForDate(dateStr);

                        if (dayEvents.length === 0) return null;

                        return (
                          <div className="mb-3 space-y-1.5 max-h-20 overflow-y-auto">
                            {dayEvents.map((event) => (
                              <div
                                key={event.id}
                                onClick={() => setViewingEvent(event)}
                                className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between group cursor-pointer hover:bg-amber-100 transition-colors"
                                title="Cliquez pour voir les détails"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="text-amber-600 text-sm flex-shrink-0"></span>
                                  <span className="text-xs font-medium text-amber-800 truncate">{event.title}</span>
                                </div>

                                {isTeamLeaderOrAbove && (
                                  <div className="flex gap-1 ml-2 flex-shrink-0">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditEvent(event);
                                      }}
                                      className="p-1 hover:bg-amber-200 rounded active:scale-95"
                                    >
                                      <Edit2 className="w-3 h-3 text-amber-600" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteEvent(event.id);
                                      }}
                                      className="p-1 hover:bg-red-100 rounded active:scale-95"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Congés */}
                      {myLeaves.length > 0 && (() => {
                        const leaveTypeLabels = {
                          paid_leave: { label: 'Congés payés', bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', sub: 'text-indigo-600' },
                          sick_leave: { label: 'Arrêt maladie', bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', sub: 'text-orange-600' },
                          unjustified: { label: 'Absence injustifiée', bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', sub: 'text-gray-600' },
                          compensatory: { label: 'Repos compensateur', bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', sub: 'text-purple-600' },
                        };
                        const cfg = leaveTypeLabels[myLeaves[0].leave_type] || leaveTypeLabels['paid_leave'];
                        return (
                          <div className={`${cfg.bg} border-2 rounded-lg p-2 mb-2`}>
                            <div className={`flex items-center gap-1.5 ${cfg.text} mb-1`}>
                              <Plane className="w-3.5 h-3.5" />
                              <span className="font-semibold text-sm">{cfg.label}</span>
                            </div>
                            {myLeaves[0].reason && (
                              <p className={`text-xs ${cfg.sub}`}>{myLeaves[0].reason}</p>
                            )}
                            {myLeaves[0].start_datetime && myLeaves[0].end_datetime && getLeavePeriod(myLeaves[0].start_datetime, myLeaves[0].end_datetime) !== 'full' && (
                              <p className={`text-[10px] ${cfg.sub} mt-1`}>
                                {getLeavePeriod(myLeaves[0].start_datetime, myLeaves[0].end_datetime) === 'morning'
                                  ? 'Demi-journée (matin)'
                                  : 'Demi-journée (après-midi)'}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Notifs Non Dupliqués (Manager only) */}
                      {isTeamLeaderOrAbove && (() => {
                        const dateStr = formatDateLocal(day.date);
                        const dayNotifs = skippedShiftNotifs.filter(n => n.date === dateStr);
                        if (dayNotifs.length === 0) return null;

                        const dismissSkippedShiftNotif = (id) => {
                          const notifs = JSON.parse(localStorage.getItem('skippedShiftNotifs') || '[]');
                          const updated = notifs.filter(n => n.id !== id);
                          localStorage.setItem('skippedShiftNotifs', JSON.stringify(updated));
                          setSkippedShiftNotifs(updated);
                          if (updated.length === 0) {
                            setShowSkippedPanel(false);
                          }
                        };

                        const toLocalISOString = (date) => {
                          const pad = (n) => (n < 10 ? '0' + n : n);
                          return date.getFullYear() +
                            '-' + pad(date.getMonth() + 1) +
                            '-' + pad(date.getDate()) +
                            'T' + pad(date.getHours()) +
                            ':' + pad(date.getMinutes());
                        };

                        return (
                          <div className="mb-3 space-y-2">
                            <div className="flex items-center gap-1.5 text-orange-600 font-semibold px-1">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-xs">Non dupliqués</span>
                            </div>
                            {dayNotifs.map((notif) => {
                              const startStr = notif.startTime
                                ? formatTimeSafe(notif.startTime, { hour: '2-digit', minute: '2-digit' })
                                : null;
                              const endStr = notif.endTime
                                ? formatTimeSafe(notif.endTime, { hour: '2-digit', minute: '2-digit' })
                                : null;

                              return (
                                <div
                                  key={notif.id}
                                  className="border-2 border-orange-400 bg-orange-50 rounded-lg p-2.5 shadow-sm"
                                >
                                  <div className="flex justify-between items-start mb-1">
                                    <div className="font-bold text-orange-800 text-xs">
                                      {notif.userName || 'Inconnu'}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dismissSkippedShiftNotif(notif.id);
                                      }}
                                      className="text-orange-400 hover:text-orange-600 p-0.5 rounded-full"
                                      title="Masquer"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>

                                  {startStr && endStr && (
                                    <div className="text-xs text-orange-700 font-semibold mb-1">
                                      {startStr} - {endStr}
                                    </div>
                                  )}

                                  {notif.roomName && (
                                    <div className="text-[10px] text-orange-600 flex items-center gap-1 mt-0.5">
                                      <Building className="w-3 h-3" />
                                      <span>{notif.roomName}</span>
                                    </div>
                                  )}

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      let endDateTimeDateStr = notif.date;
                                      if (notif.startTime && notif.endTime && notif.date) {
                                        const sd = parseDateSafe(notif.startTime);
                                        const ed = parseDateSafe(notif.endTime);
                                        const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
                                        const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
                                        const dayDiff = Math.abs(getCalendarDayOffset(endDay, startDay));
                                        if (dayDiff > 0) {
                                          const newDate = new Date(notif.date);
                                          newDate.setDate(newDate.getDate() + dayDiff);
                                          const y = newDate.getFullYear();
                                          const m = String(newDate.getMonth() + 1).padStart(2, '0');
                                          const d = String(newDate.getDate()).padStart(2, '0');
                                          endDateTimeDateStr = `${y}-${m}-${d}`;
                                        }
                                      }

                                      const startDateTime = new Date(`${notif.date}T${startStr}:00`);
                                      const endDateTime = new Date(`${endDateTimeDateStr}T${endStr}:00`);

                                      setNewShift({
                                        user_id: notif.userId || "",
                                        start_datetime: toLocalISOString(startDateTime),
                                        end_datetime: toLocalISOString(endDateTime),
                                        room_id: notif.roomId || "",
                                        break_duration: notif.breakDuration || 0,
                                      });
                                      setPendingAssignNotifId(notif.id);
                                      setShowModal(true);
                                    }}
                                    className="mt-2 w-full py-1 px-2 bg-white border border-orange-300 text-orange-600 text-[10px] font-semibold rounded-md hover:bg-orange-100 flex items-center justify-center gap-1"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Assigner
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Shifts */}
                      {myShifts.length > 0 ? (
                        <div className="space-y-1.5">
                          {myShifts.map(shift => {
                            const totalMin = Math.round((parseDateSafe(shift.end_time) - parseDateSafe(shift.start_time)) / (1000 * 60));
                            const breakDur = shift.break_duration || 0;
                            const netDuration = Math.max(0, totalMin - breakDur);
                            const hours = Math.floor(netDuration / 60);
                            const minutes = netDuration % 60;
                            const hasPendingRequest = getPendingRequestForShift(shift.id);
                            const primaryTag = shift.tags && shift.tags.length > 0 ? shift.tags[0] : null;
                            const additionalTags = shift.tags && shift.tags.length > 1 ? shift.tags.slice(1) : [];

                            return (
                              <div
                                key={shift.id}
                                onClick={() => handleShiftClick(shift)}
                                className={`relative rounded-lg p-2 cursor-pointer hover:shadow-md transition-all active:scale-95 ${selectedShiftIds.includes(shift.id) ? 'ring-2 ring-indigo-600 shadow-md ring-offset-1' : ''}`}
                                style={primaryTag ? {
                                  backgroundColor: `${primaryTag.color}15`,
                                  border: `2px solid ${primaryTag.color}`,
                                } : {
                                  backgroundColor: '#F3F4F6',
                                  border: '2px solid #9CA3AF'
                                }}
                              >
                                {selectionMode && isTeamLeaderOrAbove && (
                                  <div className="absolute top-1 right-1 z-30 bg-white/90 rounded p-0.5 shadow-sm flex items-center justify-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedShiftIds.includes(shift.id)}
                                      onChange={() => {}}
                                      className="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                      readOnly
                                    />
                                  </div>
                                )}
                                {hasPendingRequest && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white" title="Demande en attente" />
                                )}

                                {/* Additional tag color indicators */}
                                {additionalTags.length > 0 && (
                                  <div className="absolute bottom-2 left-2 flex gap-1">
                                    {additionalTags.map((tag) => (
                                      <div
                                        key={tag.id}
                                        className="w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                                        style={{ backgroundColor: tag.color }}
                                        title={tag.name}
                                      />
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0 pr-2 text-center">
                                    <div className="text-xs font-semibold" style={{ color: primaryTag ? primaryTag.color : '#6B7280' }}>
                                      {shift.start_time?.slice(11, 16)} - {shift.end_time?.slice(11, 16)}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-0.5 flex justify-center items-center gap-1">
                                      <span>{hours}h{minutes > 0 ? `${minutes}m` : ''}</span>
                                      {shift.break_duration > 0 && (
                                        <span className="text-[10px] text-amber-600 font-medium">
                                          ({shift.break_duration}m)
                                        </span>
                                      )}
                                    </div>
                                    {(shift.room_name || shift.comment) && (
                                      <div className="flex items-center justify-center mt-0.5 gap-1.5">
                                        <div className="text-[10px] text-gray-400 truncate max-w-[120px]">
                                          {shift.room_name}
                                        </div>
                                        {shift.comment && (
                                          <div className="text-blue-600 flex-shrink-0" title={shift.comment}>
                                            <MessageSquare className="w-3 h-3" />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {shift.validated && (
                                    <div className="bg-green-500 text-white text-[10px] px-1 py-0.5 rounded font-medium">
                                      Validé
                                    </div>
                                  )}
                                </div>
                                {/* Tags Display */}
                                {shift.tags && shift.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-0.5 mt-1.5">
                                    {shift.tags.map((tag) => (
                                      <span
                                        key={tag.id}
                                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                        style={{
                                          backgroundColor: tag.color,
                                          color: '#ffffff',
                                        }}
                                      >
                                        {tag.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : myLeaves.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm">
                          Pas de shift
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Vue journalière actuelle
              <>
                {/* Day Navigation Tabs */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="flex overflow-x-auto scrollbar-hide">
                    {getShiftsGroupedByDay().map((dayData, dayIndex) => {
                      const isSelected = dayIndex === selectedDayIndex;
                      const isToday = isSameDay(dayData.date, today);

                      return (
                        <button
                          key={dayData.date.toISOString()}
                          onClick={() => setSelectedDayIndex(dayIndex)}
                          className={`flex-1 min-w-[50px] py-1.5 px-0.5 text-center font-medium transition-all ${isSelected
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                            : isToday
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          <div className="text-[9px] font-semibold">{dayData.dayShort}</div>
                          <div className="text-sm font-bold">{dayData.date.getDate()}</div>
                          {isToday && !isSelected && (
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mx-auto mt-1"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* User Filter Toggle */}
                <div className="bg-white rounded-xl shadow-md p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">Affichage</span>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setShowOnlyMyShifts(true)}
                        className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${showOnlyMyShifts
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        Mes horaires
                      </button>
                      <button
                        onClick={() => setShowOnlyMyShifts(false)}
                        className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${!showOnlyMyShifts
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        Tous
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selected Day Content */}
                {(() => {
                  const selectedDay = getShiftsGroupedByDay()[selectedDayIndex];
                  const filteredShifts = getFilteredShifts(selectedDay.shifts).slice().sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                  const isToday = isSameDay(selectedDay.date, today);

                  return (
                    <div className={`bg-white rounded-xl shadow-md overflow-hidden ${isToday ? 'ring-2 ring-indigo-500' : ''
                      }`}>
                      {/* Day Header */}
                      <div className={`p-3 font-semibold flex items-center justify-between ${isToday
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700'
                        }`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Calendar className="w-5 h-5" />
                          <span>{selectedDay.dayName}</span>
                          {weatherData[formatDateLocal(selectedDay.date)] && (
                            <WeatherBadge
                              dayWeather={weatherData[formatDateLocal(selectedDay.date)]}
                              onClick={handleOpenWeatherModal}
                            />
                          )}
                        </div>
                        <span className="text-xs">
                          {selectedDay.date.getDate()}/{selectedDay.date.getMonth() + 1}
                        </span>
                      </div>

                      {/* Events Badge */}
                      {(() => {
                        const dateStr = formatDateLocal(selectedDay.date);
                        const dayEvents = getEventsForDate(dateStr);

                        if (dayEvents.length === 0) return null;

                        return (
                          <div className="mx-4 mt-3 space-y-1.5 max-h-20 overflow-y-auto">
                            {dayEvents.map((event) => (
                              <div
                                key={event.id}
                                onClick={() => setViewingEvent(event)}
                                className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between group cursor-pointer hover:bg-amber-100 transition-colors"
                                title="Cliquez pour voir les détails"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="text-amber-600 text-sm flex-shrink-0"></span>
                                  <span className="text-xs font-medium text-amber-800 truncate">{event.title}</span>
                                </div>

                                {isTeamLeaderOrAbove && (
                                  <div className="flex gap-1 ml-2 flex-shrink-0">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditEvent(event);
                                      }}
                                      className="p-1 hover:bg-amber-200 rounded active:scale-95"
                                    >
                                      <Edit2 className="w-3 h-3 text-amber-600" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteEvent(event.id);
                                      }}
                                      className="p-1 hover:bg-red-100 rounded active:scale-95"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Shifts List */}
                      <div className="p-3 space-y-2">
                        {/* Add unassigned shifts (skipped shifts) to mobile daily view for managers */}
                        {isTeamLeaderOrAbove && (() => {
                          const dateStr = formatDateLocal(selectedDay.date);
                          const dayNotifs = skippedShiftNotifs.filter(n => n.date === dateStr);
                          if (dayNotifs.length === 0) return null;

                          const dismissSkippedShiftNotif = (id) => {
                            const notifs = JSON.parse(localStorage.getItem('skippedShiftNotifs') || '[]');
                            const updated = notifs.filter(n => n.id !== id);
                            localStorage.setItem('skippedShiftNotifs', JSON.stringify(updated));
                            setSkippedShiftNotifs(updated);
                            if (updated.length === 0) {
                              setShowSkippedPanel(false);
                            }
                          };

                          const toLocalISOString = (date) => {
                            const pad = (n) => (n < 10 ? '0' + n : n);
                            return date.getFullYear() +
                              '-' + pad(date.getMonth() + 1) +
                              '-' + pad(date.getDate()) +
                              'T' + pad(date.getHours()) +
                              ':' + pad(date.getMinutes());
                          };

                          return (
                            <div className="mb-4">
                              <div className="flex items-center gap-1.5 mb-2 text-orange-600 font-semibold px-1">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-sm">Shifts non dupliqués</span>
                              </div>
                              <div className="space-y-2">
                                {dayNotifs.map((notif) => {
                                  const startStr = notif.startTime
                                    ? formatTimeSafe(notif.startTime, { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                  const endStr = notif.endTime
                                    ? formatTimeSafe(notif.endTime, { hour: '2-digit', minute: '2-digit' })
                                    : null;

                                  return (
                                    <div
                                      key={notif.id}
                                      className="border-2 border-orange-400 bg-orange-50 rounded-lg p-3 shadow-sm"
                                    >
                                      <div className="flex justify-between items-start mb-1">
                                        <div className="font-bold text-orange-800 text-sm">
                                          {notif.userName || 'Inconnu'}
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            dismissSkippedShiftNotif(notif.id);
                                          }}
                                          className="text-orange-400 hover:text-orange-600 p-1 rounded-full hover:bg-orange-100 transition-colors"
                                          title="Masquer"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>

                                      {startStr && endStr && (
                                        <div className="text-sm text-orange-700 font-semibold mb-1">
                                          {startStr} - {endStr}
                                        </div>
                                      )}

                                      {notif.roomName && (
                                        <div className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                                          <Building className="w-3 h-3" />
                                          <span>{notif.roomName}</span>
                                        </div>
                                      )}

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Pré-remplir le formulaire d'ajout
                                          let endDateTimeDateStr = notif.date;
                                          if (notif.startTime && notif.endTime && notif.date) {
                                            const sd = parseDateSafe(notif.startTime);
                                            const ed = parseDateSafe(notif.endTime);
                                            const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
                                            const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
                                            const dayDiff = Math.abs(getCalendarDayOffset(endDay, startDay));
                                            if (dayDiff > 0) {
                                              const newDate = new Date(notif.date);
                                              newDate.setDate(newDate.getDate() + dayDiff);
                                              const y = newDate.getFullYear();
                                              const m = String(newDate.getMonth() + 1).padStart(2, '0');
                                              const d = String(newDate.getDate()).padStart(2, '0');
                                              endDateTimeDateStr = `${y}-${m}-${d}`;
                                            }
                                          }

                                          const startDateTime = new Date(`${notif.date}T${startStr}:00`);
                                          const endDateTime = new Date(`${endDateTimeDateStr}T${endStr}:00`);

                                          setNewShift({
                                            user_id: notif.userId || "",
                                            start_datetime: toLocalISOString(startDateTime),
                                            end_datetime: toLocalISOString(endDateTime),
                                            room_id: notif.roomId || "",
                                            break_duration: notif.breakDuration || 0,
                                          });
                                          setPendingAssignNotifId(notif.id);
                                          setShowModal(true);
                                        }}
                                        className="mt-2 w-full py-1.5 px-3 bg-white border border-orange-300 text-orange-600 text-xs font-semibold rounded-md hover:bg-orange-100 flex items-center justify-center gap-1 transition-colors"
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                        Assigner ce shift
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {filteredShifts.length === 0 ? (
                          <div key={`empty-${selectedDay.date.getTime()}-${showOnlyMyShifts}`} className="text-center text-gray-400 py-8 text-sm">
                            {showOnlyMyShifts
                              ? 'Aucun de vos shifts ce jour'
                              : 'Aucun shift prévu'
                            }
                          </div>
                        ) : (
                          filteredShifts.map((shift) => {
                            // Skip shifts with missing time data
                            if (!shift?.start_time || !shift?.end_time) {
                              console.warn("Skipping shift with missing time data:", shift);
                              return null;
                            }

                            const shiftUser = users.find(u => u.id === shift.user_id);
                            const colors = getUserColor(shift.user_id);
                            const start = parseDateSafe(shift.start_time);
                            const end = parseDateSafe(shift.end_time);
                            const duration = Math.round((end - start) / (1000 * 60));
                            const breakDur = shift.break_duration || 0;
                            const netDuration = Math.max(0, duration - breakDur);
                            const hours = Math.floor(netDuration / 60);
                            const minutes = netDuration % 60;
                            const hasPendingRequest = getPendingRequestForShift(shift.id);
                            const isCurrentUser = shift.user_id === parseInt(localStorage.getItem("user_id"));
                            const isInteractive = isTeamLeaderOrAbove || isCurrentUser || !!shift.comment;
                            const primaryTag = shift.tags && shift.tags.length > 0 ? shift.tags[0] : null;
                            const additionalTags = shift.tags && shift.tags.length > 1 ? shift.tags.slice(1) : [];
                            const isNoShow = shift.no_show; // Check if shift is no-show

                            return (
                              <div
                                key={shift.id}
                                onClick={() => isInteractive && handleShiftClick(shift)}
                                className={`relative border-2 rounded-lg p-2 transition-transform ${isInteractive
                                  ? 'cursor-pointer active:scale-98'
                                  : 'cursor-default'
                                  } ${isNoShow ? 'opacity-50' : ''} ${selectedShiftIds.includes(shift.id) ? 'ring-2 ring-indigo-600 shadow-md ring-offset-1' : ''}`}
                                style={isNoShow ? {
                                  backgroundColor: '#FEE2E2',
                                  borderColor: '#EF4444',
                                } : (primaryTag ? {
                                  backgroundColor: `${primaryTag.color}15`,
                                  borderColor: primaryTag.color,
                                } : {
                                  backgroundColor: '#F3F4F6',
                                  borderColor: '#9CA3AF'
                                })}
                              >
                                {selectionMode && isTeamLeaderOrAbove && (
                                  <div className="absolute top-1 right-1 z-30 bg-white/90 rounded p-0.5 shadow-sm flex items-center justify-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedShiftIds.includes(shift.id)}
                                      onChange={() => {}}
                                      className="w-3.5 h-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                      readOnly
                                    />
                                  </div>
                                )}
                                {hasPendingRequest && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-white" title="Demande en attente" />
                                )}

                                {/* Draft Badge */}
                                {!shift.is_published && !isNoShow && isTeamLeaderOrAbove && (
                                  <div className="absolute -top-2 left-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 text-[9px] font-bold rounded shadow-sm flex items-center gap-0.5" title="Shift en brouillon (non publié)">
                                    🔒 BROUILLON
                                  </div>
                                )}

                                {/* No-Show Badge */}
                                {isNoShow && (
                                  <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full shadow-lg flex items-center gap-0.5" title="Absence non justifiée">
                                    <X className="w-3 h-3" />
                                    ABSENT
                                  </div>
                                )}

                                {/* Additional tag color indicators */}
                                {additionalTags.length > 0 && (
                                  <div className="absolute bottom-2 left-2 flex gap-1">
                                    {additionalTags.map((tag) => (
                                      <div
                                        key={tag.id}
                                        className="w-3 h-3 rounded-full border border-white shadow-sm"
                                        style={{ backgroundColor: tag.color }}
                                        title={tag.name}
                                      />
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white text-sm font-semibold shadow-md`}>
                                      {(shiftUser?.full_name || shiftUser?.email || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="font-medium text-sm text-gray-800">
                                        {shiftUser?.full_name || shiftUser?.email}
                                      </div>
                                      {isCurrentUser && (
                                        <div className="text-xs text-indigo-600 font-medium">Vous</div>
                                      )}
                                      {isNoShow && (
                                        <div className="text-[10px] text-red-600 font-semibold flex items-center gap-0.5">
                                          ⚠️ Non comptabilisé
                                        </div>
                                      )}
                                      {shift.tags && shift.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-0.5 mt-1">
                                          {shift.tags.map((tag) => (
                                            <span
                                              key={tag.id}
                                              className="px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                              style={{
                                                backgroundColor: tag.color,
                                                color: '#ffffff',
                                              }}
                                            >
                                              {tag.name}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-center flex-shrink-0 ml-2">
                                    <div className={`font-bold text-sm ${isNoShow ? 'line-through' : ''}`} style={!isNoShow && primaryTag ? { color: primaryTag.color } : { color: isNoShow ? '#DC2626' : '#6B7280' }}>
                                      {shift.start_time?.slice(11, 16)} - {shift.end_time?.slice(11, 16)}
                                    </div>
                                    <div className={`text-xs mt-0.5 flex justify-center items-center gap-1 ${isNoShow ? 'text-red-500 line-through' : 'text-gray-600'}`}>
                                      <span>{hours}h{minutes > 0 ? `${minutes}m` : ''}</span>
                                      {!isNoShow && shift.break_duration > 0 && (
                                        <span className="text-[10px] text-amber-600 font-medium">
                                          ({shift.break_duration}m)
                                        </span>
                                      )}
                                    </div>

                                    {(!isNoShow && (shift.room_name || shift.comment)) && (
                                      <div className="flex items-center justify-end gap-1.5 mt-0.5">
                                        <div className="text-[10px] text-gray-400 truncate max-w-[120px]">
                                          {shift.room_name}
                                        </div>
                                        {shift.comment && (
                                          <div className="text-blue-600 flex-shrink-0" title={shift.comment}>
                                            <MessageSquare className="w-3 h-3" />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {shift.validated && (
                                  <div className="flex items-center gap-1 text-xs text-green-600 mt-2">
                                    <Check className="w-3 h-3" />
                                    <span>Validé</span>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}

                        {/* Display leaves for users on this day */}
                        {!showOnlyMyShifts && sortedUsers.map(employee => {
                          const dateStr = formatDateLocal(selectedDay.date);
                          const onLeave = isUserOnLeave(employee.id, dateStr);
                          const hasShift = filteredShifts.some(s => s.user_id === employee.id);

                          // Only show leave indicator if user doesn't have a shift
                          if (onLeave && !hasShift) {
                            const colors = getUserColor(employee.id);
                            const isDayOff = onLeave.is_day_off;

                            return (
                              <div
                                key={`leave-${employee.id}`}
                                className={`${isDayOff ? 'bg-gray-50 border-gray-200' : colors.bg + ' ' + colors.border} border-2 rounded-lg p-3 opacity-75`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white text-sm font-semibold`}>
                                    {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm text-gray-800">
                                      {employee.full_name || employee.email}
                                    </div>
                                    {isDayOff ? (
                                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                        <Coffee className="w-3 h-3" />
                                        <span>Jour de repos</span>
                                        {isTeamLeaderOrAbove && (
                                          <button
                                            onClick={() => deleteDayOff(onLeave.id)}
                                            className="ml-auto text-red-500 hover:underline"
                                          >
                                            Supprimer
                                          </button>
                                        )}
                                      </div>
                                    ) : (() => {
                                      const leaveLabels = {
                                        paid_leave: 'Congés payés',
                                        sick_leave: 'Arrêt maladie',
                                        work_accident: 'Accident du travail',
                                        unjustified: 'Absence injustifiée',
                                        compensatory: 'Repos compensateur',
                                      };
                                      const label = leaveLabels[onLeave.leave_type] || 'En congé';
                                      return (
                                        <div className="flex items-center gap-1 text-xs text-indigo-600 mt-1">
                                          <Plane className="w-3 h-3" />
                                          <span>{label}</span>
                                          {isManager && (
                                            <button
                                              onClick={() => requestDeleteLeave(onLeave)}
                                              className="ml-auto text-red-500 hover:underline"
                                            >
                                              Supprimer
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  );
                })()}

                {users.length === 0 && (
                  <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-400">
                    Aucun employé trouvé
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Modal Création Shift - Responsive */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col transform transition-all overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-3 sm:p-5 border-b border-gray-200 bg-white flex-shrink-0 rounded-t-2xl">
              <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {modalTab === 'shift' ? 'Créer un nouveau shift' : 'Ajouter une absence'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setPendingAssignNotifId(null);
                  setModalTab('shift');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 touch-manipulation"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-0">
              {/* Bandeau contextuel : assignation d'un shift non dupliqué */}
              {pendingAssignNotifId && (
                <div className="mx-3 mt-3 sm:mx-5 sm:mt-5 flex items-start gap-2 bg-orange-50 border border-orange-300 rounded-lg p-2.5">
                  <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-orange-800">Assignation d'un shift non dupliqué</div>
                    <div className="text-[10px] text-orange-600 mt-0.5">
                      {skippedShiftNotifs.find(n => n.id === pendingAssignNotifId)?.message || ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Onglets (Tabs) */}
              <div className="flex border-b border-gray-100 mt-2 px-3 sm:px-5">
                <button
                  onClick={() => setModalTab('shift')}
                  className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${modalTab === 'shift' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Shift
                </button>
                <button
                  onClick={() => setModalTab('absence')}
                  className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${modalTab === 'absence' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Absence
                </button>
              </div>

              {/* Formulaires */}
              <div className="p-3 sm:p-5">
                {modalTab === 'shift' ? (
                  <form onSubmit={handleCreateShift} className="space-y-3">
                    {/* Employé */}
                    <div>
                      <label htmlFor="shift-user-id" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Employé
                      </label>
                      <select
                        id="shift-user-id"
                        name="user_id"
                        value={newShift.user_id}
                        onChange={(e) => setNewShift({ ...newShift, user_id: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                        required
                      >
                        <option value="">Sélectionner un employé</option>
                        {sortedUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Start DateTime */}
                    <div>
                      <label htmlFor="shift-start-datetime" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Début du shift
                      </label>
                      <input
                        id="shift-start-datetime"
                        name="start_datetime"
                        type="datetime-local"
                        value={newShift.start_datetime}
                        onChange={(e) => {
                          const start = e.target.value;
                          const end = newShift.end_datetime;
                          let breakDuration = newShift.break_duration;
                          if (start && end) {
                            const diffMin = (new Date(end) - new Date(start)) / (1000 * 60);
                            breakDuration = diffMin >= 360 ? 30 : 0;
                          }
                          setNewShift({ ...newShift, start_datetime: start, break_duration: breakDuration });
                        }}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                        required
                      />
                    </div>

                    {/* End DateTime */}
                    <div>
                      <label htmlFor="shift-end-datetime" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Fin du shift
                      </label>
                      <input
                        id="shift-end-datetime"
                        name="end_datetime"
                        type="datetime-local"
                        value={newShift.end_datetime}
                        onChange={(e) => {
                          const end = e.target.value;
                          const start = newShift.start_datetime;
                          let breakDuration = newShift.break_duration;
                          if (start && end) {
                            const diffMin = (new Date(end) - new Date(start)) / (1000 * 60);
                            breakDuration = diffMin >= 360 ? 30 : 0;
                          }
                          setNewShift({ ...newShift, end_datetime: end, break_duration: breakDuration });
                        }}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                        required
                      />
                    </div>

                    {/* Pause (Break Duration) */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      <label htmlFor="shift-break-duration" className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <Coffee className="w-4 h-4" />
                        Temps de pause (minutes)
                      </label>
                      <input
                        id="shift-break-duration"
                        name="break_duration"
                        type="number"
                        min="0"
                        step="5"
                        value={newShift.break_duration}
                        onChange={(e) => setNewShift({ ...newShift, break_duration: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-base bg-white"
                      />
                      {newShift.start_datetime && newShift.end_datetime && (() => {
                        const totalMin = (new Date(newShift.end_datetime) - new Date(newShift.start_datetime)) / (1000 * 60);
                        const netMin = Math.max(0, totalMin - (parseInt(newShift.break_duration) || 0));
                        const h = Math.floor(netMin / 60);
                        const m = netMin % 60;
                        return (
                          <p className="text-xs text-amber-700 mt-1.5">
                            Total brut : {Math.floor(totalMin / 60)}h{totalMin % 60 > 0 ? `${totalMin % 60}m` : ''}
                            {' → '}Net : <strong>{h}h{m > 0 ? `${m}m` : ''}</strong>
                          </p>
                        );
                      })()}
                    </div>

                    {/* Commentaire */}
                    <div className="mt-4">
                      <label htmlFor="shift-comment" className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Commentaire (optionnel)
                      </label>
                      <textarea
                        id="shift-comment"
                        name="comment"
                        value={newShift.comment || ""}
                        onChange={(e) => setNewShift({ ...newShift, comment: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base bg-white resize-none"
                        placeholder="Note pour ce shift..."
                      ></textarea>
                    </div>

                    {/* Room Selector */}
                    <div>
                      <label htmlFor="shift-room-id" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Salle (optionnel)
                      </label>
                      <select
                        id="shift-room-id"
                        name="room_id"
                        value={newShift.room_id}
                        onChange={(e) => setNewShift({ ...newShift, room_id: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                      >
                        <option value="">Aucune salle</option>
                        {rooms.map((room) => (
                          <option key={room.id} value={room.id}>
                            {room.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Tag Selector */}
                    {tags.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Étiquettes (optionnel)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => {
                                setSelectedTagIds(prev =>
                                  prev.includes(tag.id)
                                    ? prev.filter(id => id !== tag.id)
                                    : [...prev, tag.id]
                                );
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedTagIds.includes(tag.id)
                                ? 'ring-2 ring-offset-2 ring-indigo-500'
                                : 'hover:opacity-80'
                                }`}
                              style={{
                                backgroundColor: tag.color,
                                color: '#ffffff',
                              }}
                            >
                              {selectedTagIds.includes(tag.id) && <Check className="w-3 h-3 inline mr-1" />}
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex flex-col gap-2 pt-3 border-t border-gray-100 mt-3">
                      <button
                        type="button"
                        onClick={createDayOff}
                        disabled={loading || !newShift.user_id}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation disabled:opacity-50"
                      >
                        <Plane className="w-4 h-4 text-gray-500" />
                        Marquer comme Repos
                      </button>

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => { setShowModal(false); setPendingAssignNotifId(null); }}
                          className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 transition-colors touch-manipulation"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation"
                        >
                          {loading ? "Création..." : "Créer le shift"}
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  /* FORMULAIRE D'ABSENCE */
                  <form onSubmit={handleCreateAbsence} className="space-y-4">
                    <div>
                      <label htmlFor="abs-user-select" className="block text-xs font-medium text-gray-700 mb-1.5">Employé</label>
                      <select
                        id="abs-user-select"
                        name="user_id"
                        value={absenceForm.user_id}
                        onChange={(e) => setAbsenceForm({ ...absenceForm, user_id: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base"
                        required
                      >
                        <option value="">Sélectionner un employé</option>
                        {sortedUsers.map(user => (
                          <option key={user.id} value={user.id}>{user.full_name || user.email}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="abs-leave-type" className="block text-xs font-medium text-gray-700 mb-1.5">Type d'absence</label>
                      <select
                        id="abs-leave-type"
                        name="leave_type"
                        value={absenceForm.leave_type}
                        onChange={(e) => setAbsenceForm({ ...absenceForm, leave_type: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base"
                      >
                        {LEAVE_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>

                    <HalfDaySwitches form={absenceForm} setForm={setAbsenceForm} disabledSameDay={true} />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="abs-start-date" className="block text-xs font-medium text-gray-700 mb-1.5">Date de début</label>
                        <input
                          id="abs-start-date"
                          name="start_date"
                          type="date"
                          value={absenceForm.start_date}
                          onChange={(e) => setAbsenceForm({ ...absenceForm, start_date: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="abs-end-date" className="block text-xs font-medium text-gray-700 mb-1.5">Date de fin</label>
                        <input
                          id="abs-end-date"
                          name="end_date"
                          type="date"
                          value={absenceForm.end_date}
                          onChange={(e) => setAbsenceForm({ ...absenceForm, end_date: e.target.value })}
                          min={absenceForm.start_date}
                          className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                     <div>
                      <label htmlFor="abs-reason" className="block text-xs font-medium text-gray-700 mb-1.5">Raison (optionnel)</label>
                      <textarea
                        id="abs-reason"
                        name="reason"
                        value={absenceForm.reason}
                        onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                        placeholder="Ex: Maladie, etc."
                      />
                    </div>

                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-800">⚠️ Cette absence sera <strong>automatiquement approuvée</strong>.</p>
                    </div>

                    <div className="flex gap-3 pt-3 border-t border-gray-100 mt-3">
                      <button
                        type="button"
                        onClick={() => { setShowModal(false); setModalTab('shift'); }}
                        className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={absenceLoading || !absenceForm.user_id || !absenceForm.start_date || !absenceForm.end_date}
                        className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-lg disabled:opacity-50 touch-manipulation"
                      >
                        {absenceLoading ? "Enregistrement..." : "Créer l'absence"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shift Painter Template Config Modal */}
      {showPaintConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col transform transition-all overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-3 sm:p-5 border-b border-gray-200 bg-white flex-shrink-0 rounded-t-2xl">
              <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Configuration du Tampon
              </h2>
              <button
                onClick={() => setShowPaintConfigModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 touch-manipulation"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-3 sm:p-5 space-y-4">
              {/* Start Time */}
              <div>
                <label htmlFor="paint-start-time" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Heure de début
                </label>
                <input
                  id="paint-start-time"
                  name="start_time"
                  type="time"
                  value={paintTemplate.start_time}
                  onChange={(e) => {
                    const start = e.target.value;
                    let breakDuration = paintTemplate.break_duration;
                    if (start && paintTemplate.end_time) {
                      const [sh, sm] = start.split(':').map(Number);
                      const [eh, em] = paintTemplate.end_time.split(':').map(Number);
                      let diffMin = (eh * 60 + em) - (sh * 60 + sm);
                      if (diffMin < 0) diffMin += 24 * 60;
                      breakDuration = diffMin >= 360 ? 30 : 0;
                    }
                    setPaintTemplate({ ...paintTemplate, start_time: start, break_duration: breakDuration });
                  }}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                  required
                />
              </div>

              {/* End Time */}
              <div>
                <label htmlFor="paint-end-time" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Heure de fin
                </label>
                <input
                  id="paint-end-time"
                  name="end_time"
                  type="time"
                  value={paintTemplate.end_time}
                  onChange={(e) => {
                    const end = e.target.value;
                    let breakDuration = paintTemplate.break_duration;
                    if (paintTemplate.start_time && end) {
                      const [sh, sm] = paintTemplate.start_time.split(':').map(Number);
                      const [eh, em] = end.split(':').map(Number);
                      let diffMin = (eh * 60 + em) - (sh * 60 + sm);
                      if (diffMin < 0) diffMin += 24 * 60;
                      breakDuration = diffMin >= 360 ? 30 : 0;
                    }
                    setPaintTemplate({ ...paintTemplate, end_time: end, break_duration: breakDuration });
                  }}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                  required
                />
              </div>

              {/* Temps de pause */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                <label htmlFor="paint-break-duration" className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Coffee className="w-4 h-4" />
                  Temps de pause (minutes)
                </label>
                <input
                  id="paint-break-duration"
                  name="break_duration"
                  type="number"
                  min="0"
                  step="5"
                  value={paintTemplate.break_duration}
                  onChange={(e) => setPaintTemplate({ ...paintTemplate, break_duration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-base bg-white"
                />
                {paintTemplate.start_time && paintTemplate.end_time && (() => {
                  const [sh, sm] = paintTemplate.start_time.split(':').map(Number);
                  const [eh, em] = paintTemplate.end_time.split(':').map(Number);
                  let totalMin = (eh * 60 + em) - (sh * 60 + sm);
                  if (totalMin < 0) totalMin += 24 * 60;
                  const netMin = Math.max(0, totalMin - (parseInt(paintTemplate.break_duration) || 0));
                  const h = Math.floor(netMin / 60);
                  const m = netMin % 60;
                  return (
                    <p className="text-xs text-amber-700 mt-1.5">
                      Total brut : {Math.floor(totalMin / 60)}h{totalMin % 60 > 0 ? `${totalMin % 60}m` : ''}
                      {' → '}Net : <strong>{h}h{m > 0 ? `${m}m` : ''}</strong>
                    </p>
                  );
                })()}
              </div>

              {/* Commentaire */}
              <div>
                <label htmlFor="paint-comment" className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Commentaire (optionnel)
                </label>
                <textarea
                  id="paint-comment"
                  name="comment"
                  value={paintTemplate.comment}
                  onChange={(e) => setPaintTemplate({ ...paintTemplate, comment: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-base bg-white resize-none"
                  placeholder="Note pour ce shift..."
                ></textarea>
              </div>

              {/* Room Selector */}
              <div>
                <label htmlFor="paint-room-id" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Salle (optionnel)
                </label>
                <select
                  id="paint-room-id"
                  name="room_id"
                  value={paintTemplate.room_id}
                  onChange={(e) => setPaintTemplate({ ...paintTemplate, room_id: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-base"
                >
                  <option value="">Aucune salle</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag Selector */}
              {tags.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Étiquettes (optionnel)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          setPaintTemplate(prev => {
                            const activeTags = prev.tag_ids || [];
                            return {
                              ...prev,
                              tag_ids: activeTags.includes(tag.id)
                                ? activeTags.filter(id => id !== tag.id)
                                : [...activeTags, tag.id]
                            };
                          });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${(paintTemplate.tag_ids || []).includes(tag.id)
                          ? 'ring-2 ring-offset-2 ring-purple-500'
                          : 'hover:opacity-80'
                        }`}
                        style={{
                          backgroundColor: tag.color,
                          color: '#ffffff',
                        }}
                      >
                        {(paintTemplate.tag_ids || []).includes(tag.id) && <Check className="w-3 h-3 inline mr-1" />}
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions Buttons */}
              <div className="flex gap-3 pt-3 border-t border-gray-100 mt-3">
                <button
                  type="button"
                  onClick={() => setShowPaintConfigModal(false)}
                  className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!paintTemplate.start_time || !paintTemplate.end_time) {
                      alert("Veuillez choisir un horaire valide");
                      return;
                    }
                    setPaintMode(true);
                    setSelectionMode(false);
                    setShowPaintConfigModal(false);
                  }}
                  className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200"
                >
                  Activer le tampon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shift Action Modal */}
      {shiftActionModal.show && shiftActionModal.shift && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col transform transition-all overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-3 sm:p-5 border-b border-gray-200 bg-white flex-shrink-0 rounded-t-2xl">
              <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {shiftActionModal.mode === "edit" ? "Modifier le shift" : shiftActionModal.mode === "view-request" ? "Détails de la demande" : "Demander une modification"}
              </h2>
              <button
                onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 touch-manipulation"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Shift Details */}
            <div className="overflow-y-auto flex-1 p-3 sm:p-5 space-y-3">
              {/* Employee Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getUserColor(shiftActionModal.shift.user_id).gradient} flex items-center justify-center text-white font-semibold shadow-md`}>
                  {(shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    {shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || `User ${shiftActionModal.shift.user_id}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDateSafe(shiftActionModal.shift.start_time, {
                      weekday: "long",
                      day: "numeric",
                      month: "long"
                    })}
                  </div>
                </div>
              </div>

              {/* Manager Edit Mode */}
              {shiftActionModal.mode === "edit" && (() => {
                const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);

                return (
                  <>
                    {/* Pending Request Alert */}
                    {pendingRequest && (
                      <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                          <h3 className="font-semibold text-orange-800">Demande de modification en attente</h3>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p>Nouvelles heures proposées :</p>
                          <p className="font-medium mt-1">
                            {pendingRequest.start_time?.slice(11, 16) || '??:??'} - {pendingRequest.end_time?.slice(11, 16) || '??:??'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Demandé le {formatDateSafe(pendingRequest.created_at)}
                          </p>
                        </div>
                      </div>
                    )}

                    {!pendingRequest && (
                      <>
                        {/* User Transfer Selector (Manager only) */}
                        {isTeamLeaderOrAbove && (
                          <div>
                            <label htmlFor="edit-user-select" className="block text-sm font-medium text-gray-700 mb-2">
                              Employé
                            </label>
                            <select
                              id="edit-user-select"
                              name="user_id"
                              value={editShift.user_id}
                              onChange={(e) => setEditShift({ ...editShift, user_id: e.target.value })}
                              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                            >
                              {sortedUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.full_name || u.email}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label htmlFor="edit-start-datetime" className="block text-sm font-medium text-gray-700 mb-2">
                            Début du shift
                          </label>
                          <input
                            id="edit-start-datetime"
                            name="start_datetime"
                            type="datetime-local"
                            value={editShift.start_datetime}
                            onChange={(e) => setEditShift({ ...editShift, start_datetime: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-end-datetime" className="block text-sm font-medium text-gray-700 mb-2">
                            Fin du shift
                          </label>
                          <input
                            id="edit-end-datetime"
                            name="end_datetime"
                            type="datetime-local"
                            value={editShift.end_datetime}
                            onChange={(e) => setEditShift({ ...editShift, end_datetime: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                          />
                        </div>
                      </>
                    )}

                    {/* Room Selector (Edit Mode) */}
                    {!pendingRequest && (
                      <div className="mt-4">
                        <label htmlFor="edit-room-select" className="block text-sm font-medium text-gray-700 mb-2">
                          Salle (optionnel)
                        </label>
                        <select
                          id="edit-room-select"
                          name="room_id"
                          value={editShift.room_id}
                          onChange={(e) => setEditShift({ ...editShift, room_id: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                        >
                          <option value="">Aucune salle</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Break Duration (Edit Mode) */}
                    {!pendingRequest && (
                      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <label htmlFor="edit-break-duration" className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                          <Coffee className="w-4 h-4" />
                          Temps de pause (minutes)
                        </label>
                        <input
                          id="edit-break-duration"
                          name="break_duration"
                          type="number"
                          min="0"
                          step="5"
                          value={editShift.break_duration}
                          onChange={(e) => setEditShift({ ...editShift, break_duration: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-base bg-white"
                        />
                        {editShift.start_datetime && editShift.end_datetime && (() => {
                          const s = parseDateSafe(editShift.start_datetime);
                          const e = parseDateSafe(editShift.end_datetime);
                          if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return null;
                          const totalMin = (e - s) / (1000 * 60);
                          if (isNaN(totalMin) || totalMin < 0) return null;
                          const netMin = Math.max(0, totalMin - (parseInt(editShift.break_duration) || 0));
                          const h = Math.floor(netMin / 60);
                          const m = netMin % 60;
                          const brutH = Math.floor(totalMin / 60);
                          const brutM = totalMin % 60 > 0 ? `${totalMin % 60}m` : '';
                          const netM = m > 0 ? `${m}m` : '';
                          return (
                            <p className="text-xs text-amber-700 mt-1.5">
                              <span>{`Total brut : ${brutH}h${brutM} → Net : `}</span>
                              <strong>{`${h}h${netM}`}</strong>
                            </p>
                          );
                        })()}
                      </div>
                    )}

                    {/* Commentaire (Edit Mode) */}
                    <div className="mt-4">
                      <label htmlFor="edit-comment" className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Commentaire (optionnel)
                      </label>
                      <textarea
                        id="edit-comment"
                        name="comment"
                        value={editShift.comment || ""}
                        onChange={(e) => setEditShift({ ...editShift, comment: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base bg-white resize-none"
                        placeholder="Note pour ce shift..."
                      ></textarea>
                    </div>

                    {/* Tag Selector (only show when not viewing pending request) */}
                    {!pendingRequest && tags.length > 0 && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Étiquettes (optionnel)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => {
                                setSelectedTagIds(prev =>
                                  prev.includes(tag.id)
                                    ? prev.filter(id => id !== tag.id)
                                    : [...prev, tag.id]
                                );
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedTagIds.includes(tag.id)
                                ? 'ring-2 ring-offset-2 ring-indigo-500'
                                : 'hover:opacity-80'
                                }`}
                              style={{
                                backgroundColor: tag.color,
                                color: '#ffffff',
                              }}
                            >
                              {selectedTagIds.includes(tag.id) && <Check className="w-3 h-3 inline mr-1" />}
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No-Show Toggle (Manager only, not for pending requests) */}
                    {!pendingRequest && isTeamLeaderOrAbove && (
                      <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-3 sm:p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 text-sm">Absence non justifiée</div>
                            <div className="text-xs text-gray-600 truncate">
                              Heures non comptabilisées
                            </div>
                          </div>
                          <div className="flex items-center pl-2">
                            <input
                              type="checkbox"
                              checked={editShift?.no_show || false}
                              onChange={(e) => setEditShift({ ...editShift, no_show: e.target.checked })}
                              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer shadow-sm"
                            />
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-3">
                      {pendingRequest ? (
                        <>
                          <button
                            onClick={handleRejectRequest}
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors touch-manipulation disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Refuser
                          </button>
                          <button
                            onClick={handleModifyFromRequest}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          >
                            <Edit2 className="w-4 h-4" />
                            {loading ? "..." : "Modifier"}
                          </button>
                          <button
                            onClick={handleApproveRequest}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          >
                            <Check className="w-4 h-4" />
                            {loading ? "..." : "Valider"}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleDeleteShift}
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors touch-manipulation disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                          {/* Validation button for past shifts */}
                          {(() => {
                            const shiftEnd = parseDateSafe(shiftActionModal.shift.end_time);
                            const now = new Date();
                            const isPast = shiftEnd < now;
                            const isValidated = shiftActionModal.shift.validated;

                            if (isPast && !isValidated) {
                              return (
                                <button
                                  onClick={() => {
                                    handleValidateSingleShift(shiftActionModal.shift.id);
                                    setShiftActionModal({ show: false, shift: null, mode: null });
                                  }}
                                  disabled={loading}
                                  className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors touch-manipulation disabled:opacity-50"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Valider
                                </button>
                              );
                            }
                            return null;
                          })()}
                          <button
                            onClick={handleUpdateShift}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation"
                          >
                            <Edit2 className="w-4 h-4" />
                            {loading ? "Modification..." : "Modifier"}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* User Request Mode */}
              {shiftActionModal.mode === "request" && (
                <>
                  {shiftActionModal.shift.comment && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm mb-4 flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-600 block mb-1">Commentaire du manager</span>
                        {shiftActionModal.shift.comment}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label htmlFor="req-start-time" className="block text-sm font-medium text-gray-700 mb-2">
                        Nouveau début
                      </label>
                      <input
                        id="req-start-time"
                        name="start_time"
                        type="datetime-local"
                        value={requestModification.start_time}
                        onChange={(e) => setRequestModification({ ...requestModification, start_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                      />
                    </div>
                    <div>
                      <label htmlFor="req-end-time" className="block text-sm font-medium text-gray-700 mb-2">
                        Nouvelle fin
                      </label>
                      <input
                        id="req-end-time"
                        name="end_time"
                        type="datetime-local"
                        value={requestModification.end_time}
                        onChange={(e) => setRequestModification({ ...requestModification, end_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                      />
                    </div>
                  </div>
                  
                  {/* Break Duration (Request Mode) */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3 sm:mt-4">
                    <label htmlFor="req-break-duration" className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <Coffee className="w-4 h-4" />
                      Temps de pause (minutes)
                    </label>
                    <input
                      id="req-break-duration"
                      name="break_duration"
                      type="number"
                      min="0"
                      step="5"
                      value={requestModification.break_duration}
                      onChange={(e) => setRequestModification({ ...requestModification, break_duration: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-base bg-white"
                    />
                    {requestModification.start_time && requestModification.end_time && (() => {
                      const s = parseDateSafe(requestModification.start_time);
                      const e = parseDateSafe(requestModification.end_time);
                      if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return null;
                      const totalMin = (e - s) / (1000 * 60);
                      if (isNaN(totalMin) || totalMin < 0) return null;
                      const netMin = Math.max(0, totalMin - (parseInt(requestModification.break_duration) || 0));
                      const h = Math.floor(netMin / 60);
                      const m = netMin % 60;
                      const brutH = Math.floor(totalMin / 60);
                      const brutM = totalMin % 60 > 0 ? `${totalMin % 60}m` : '';
                      const netM = m > 0 ? `${m}m` : '';
                      return (
                        <p className="text-xs text-amber-700 mt-1.5">
                          <span>{`Total brut : ${brutH}h${brutM} → Net : `}</span>
                          <strong>{`${h}h${netM}`}</strong>
                        </p>
                      );
                    })()}
                  </div>

                  {/* Action Button for User */}
                  <div className="flex gap-2 pt-3">
                    <button
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                      className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleRequestModification}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation"
                    >
                      <Send className="w-4 h-4" />
                      {loading ? "Envoi..." : "Envoyer la demande"}
                    </button>
                  </div>
                </>
              )}

              {/* View Request Mode (Non-Manager viewing their own request) */}
              {shiftActionModal.mode === "view-request" && shiftActionModal.request && (
                <>
                  {/* Request Status Badge */}
                  <div className="flex items-center justify-center mb-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${shiftActionModal.request.status === 'pending'
                      ? 'bg-orange-100 text-orange-700 border-2 border-orange-200'
                      : shiftActionModal.request.status === 'approved'
                        ? 'bg-green-100 text-green-700 border-2 border-green-200'
                        : 'bg-red-100 text-red-700 border-2 border-red-200'
                      }`}>
                      <AlertCircle className="w-4 h-4" />
                      {shiftActionModal.request.status === 'pending' && 'En attente'}
                      {shiftActionModal.request.status === 'approved' && 'Approuvée'}
                      {shiftActionModal.request.status === 'rejected' && 'Rejetée'}
                    </span>
                  </div>

                  {shiftActionModal.shift.comment && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm mb-4 flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-600 block mb-1">Commentaire du manager</span>
                        {shiftActionModal.shift.comment}
                      </div>
                    </div>
                  )}

                  {/* Current Times */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Horaires actuels</h3>
                    <div className="flex items-center justify-center gap-2 text-lg font-medium text-gray-800">
                      <span>{shiftActionModal.shift.start_time?.slice(11, 16) || '??:??'}</span>
                      <span className="text-gray-400">→</span>
                      <span>{shiftActionModal.shift.end_time?.slice(11, 16) || '??:??'}</span>
                    </div>
                  </div>

                  {/* Requested Times */}
                  <div className="bg-indigo-50 rounded-lg p-4 mb-4 border-2 border-indigo-200">
                    <h3 className="text-sm font-semibold text-indigo-700 mb-3">Horaires demandés</h3>
                    <div className="flex items-center justify-center gap-2 text-lg font-medium text-indigo-800">
                      <span>{shiftActionModal.request.start_time?.slice(11, 16) || '??:??'}</span>
                      <span className="text-indigo-400">→</span>
                      <span>{shiftActionModal.request.end_time?.slice(11, 16) || '??:??'}</span>
                    </div>
                  </div>

                  {/* Reason (if exists) */}
                  {shiftActionModal.request.reason && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Raison</h3>
                      <p className="text-sm text-gray-600">{shiftActionModal.request.reason}</p>
                    </div>
                  )}

                  {/* Request Date */}
                  <div className="text-xs text-gray-500 text-center mb-4">
                    Demandé le {formatDateSafe(shiftActionModal.request.created_at, {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </div>

                  {/* Close Button */}
                  <div className="flex gap-2 pt-3">
                    <button
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null, request: null })}
                      className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                    >
                      Fermer
                    </button>
                  </div>
                </>
              )}

              {/* View Comment Mode (Non-Manager viewing a shift with comment) */}
              {shiftActionModal.mode === "view-comment" && (
                <>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm mb-4 flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-600 block mb-1">Commentaire sur ce shift</span>
                      {shiftActionModal.shift.comment}
                    </div>
                  </div>
                  
                  {/* Close Button */}
                  <div className="flex gap-2 pt-3">
                    <button
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                      className="flex-1 px-3 py-2.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                    >
                      Fermer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Duplicate Week Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Copy className="w-5 h-5 text-indigo-600" />
                Dupliquer la semaine
              </h3>
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 sm:p-4 space-y-4 overflow-y-auto flex-1">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm text-blue-800">
                <p className="font-medium mb-1">Semaine source :</p>
                <p>{formatDateRange(weekStart)}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="dup-offset" className="block text-sm font-medium text-gray-700 mb-1">
                    Décalage (semaines)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDuplicateData(prev => ({ ...prev, targetWeekOffset: prev.targetWeekOffset - 1 }))}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <input
                      id="dup-offset"
                      name="targetWeekOffset"
                      type="number"
                      value={duplicateData.targetWeekOffset}
                      onChange={(e) => setDuplicateData(prev => ({ ...prev, targetWeekOffset: parseInt(e.target.value) || 0 }))}
                      className="block w-full text-center rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setDuplicateData(prev => ({ ...prev, targetWeekOffset: prev.targetWeekOffset + 1 }))}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Cible : {(() => {
                      const d = new Date(weekStart);
                      d.setDate(d.getDate() + (duplicateData.targetWeekOffset * 7));
                      return formatDateRange(d);
                    })()}
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Employés (optionnel)
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (duplicateData.selectedUserIds.length === sortedUsers.length) {
                          setDuplicateData(prev => ({ ...prev, selectedUserIds: [] }));
                        } else {
                          setDuplicateData(prev => ({ ...prev, selectedUserIds: sortedUsers.map(u => u.id) }));
                        }
                      }}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {duplicateData.selectedUserIds.length === sortedUsers.length ? "Tout désélectionner" : "Tout sélectionner"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Laisser vide pour tous les employés</p>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {sortedUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={duplicateData.selectedUserIds.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDuplicateData(prev => ({ ...prev, selectedUserIds: [...prev.selectedUserIds, u.id] }));
                            } else {
                              setDuplicateData(prev => ({ ...prev, selectedUserIds: prev.selectedUserIds.filter(id => id !== u.id) }));
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{u.full_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDuplicateModal(false)}
                  className="px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDuplicateWeek}
                  disabled={duplicateLoading}
                  className="px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {duplicateLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Duplication...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Dupliquer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag Management Modal */}
      {showTagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-indigo-600" />
                Gérer les étiquettes
              </h3>
              <button
                onClick={() => setShowTagModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 sm:p-4 space-y-4 overflow-y-auto flex-1">
              {/* Create/Edit Tag Form */}
              <form onSubmit={editingTag ? updateTag : createTag} className="space-y-4">
                <div>
                  <label htmlFor="tag-name" className="block text-sm font-medium text-gray-700 mb-2">
                    {editingTag ? "Modifier l'étiquette" : "Nom de l'étiquette"}
                  </label>
                  <input
                    id="tag-name"
                    name="name"
                    type="text"
                    value={editingTag ? editingTag.name : newTag.name}
                    onChange={(e) => editingTag
                      ? setEditingTag({ ...editingTag, name: e.target.value })
                      : setNewTag({ ...newTag, name: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Main Bar, VIP, Soirée..."
                    required
                  />
                </div>

                <div>
                  <label htmlFor="tag-color" className="block text-sm font-medium text-gray-700 mb-2">
                    Couleur
                  </label>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      id="tag-color"
                      name="color"
                      type="color"
                      value={editingTag ? editingTag.color : newTag.color}
                      onChange={(e) => editingTag
                        ? setEditingTag({ ...editingTag, color: e.target.value })
                        : setNewTag({ ...newTag, color: e.target.value })
                      }
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={editingTag ? editingTag.color : newTag.color}
                      onChange={(e) => editingTag
                        ? setEditingTag({ ...editingTag, color: e.target.value })
                        : setNewTag({ ...newTag, color: e.target.value })
                      }
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                      placeholder="#3B82F6"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>

                  {/* Color Palette Suggestions */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Couleurs suggérées :</p>
                    <div className="grid grid-cols-5 gap-2">
                      {colorPalette.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => editingTag
                            ? setEditingTag({ ...editingTag, color })
                            : setNewTag({ ...newTag, color })
                          }
                          className="w-full h-8 rounded border-2 border-gray-200 hover:border-indigo-400 transition-all hover:scale-110"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  {editingTag && (
                    <button
                      type="button"
                      onClick={() => setEditingTag(null)}
                      className="flex-1 px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {editingTag ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {loading ? (editingTag ? "Modification..." : "Création...") : (editingTag ? "Modifier" : "Créer l'étiquette")}
                  </button>
                </div>
              </form>

              {/* Existing Tags List */}
              {tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Étiquettes existantes</h4>
                  <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
                    {tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="group relative flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all hover:shadow-md"
                        style={{
                          backgroundColor: `${tag.color}15`,
                          borderColor: tag.color,
                        }}
                      >
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-sm font-medium" style={{ color: tag.color }}>
                          {tag.name}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingTag({ ...tag })}
                            className="p-1 hover:bg-white/50 rounded transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-3 h-3" style={{ color: tag.color }} />
                          </button>
                          <button
                            onClick={() => deleteTag(tag.id)}
                            className="p-1 hover:bg-white/50 rounded transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Room Management Modal */}
      {showRoomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Building className="w-5 h-5 text-indigo-600" />
                Gérer les salles
              </h3>
              <button
                onClick={() => setShowRoomModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 sm:p-4 space-y-4 overflow-y-auto flex-1">
              {/* Create/Edit Room Form */}
              <form onSubmit={editingRoom ? updateRoom : createRoom} className="space-y-4">
                <div>
                  <label htmlFor="room-name" className="block text-sm font-medium text-gray-700 mb-2">
                    {editingRoom ? "Modifier la salle" : "Nom de la salle"}
                  </label>
                  <input
                    id="room-name"
                    name="name"
                    type="text"
                    value={editingRoom ? editingRoom.name : newRoom.name}
                    onChange={(e) => editingRoom
                      ? setEditingRoom({ ...editingRoom, name: e.target.value })
                      : setNewRoom({ ...newRoom, name: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Bar Principal, Terrasse, Cuisine..."
                    required
                  />
                </div>

                <div className="flex gap-2">
                  {editingRoom && (
                    <button
                      type="button"
                      onClick={() => setEditingRoom(null)}
                      className="flex-1 px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {editingRoom ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {loading ? (editingRoom ? "Modification..." : "Création...") : (editingRoom ? "Modifier" : "Créer la salle")}
                  </button>
                </div>
              </form>

              {/* Existing Rooms List */}
              {rooms.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Salles existantes</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {rooms.map((room, index) => {
                      const roomColor = getRoomColor(room.id);
                      const roomHours = calculateRoomHours(room.id);
                      return (
                        <div
                          key={room.id}
                          className="group relative flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 transition-all hover:shadow-md bg-white"
                          style={{
                            borderColor: roomColor,
                            backgroundColor: `${roomColor}08`,
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: roomColor }}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">
                                {room.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {parseFloat(roomHours.toFixed(2))}h cette semaine
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setEditingRoom({ ...room })}
                              className="p-1.5 hover:bg-white/80 rounded transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" style={{ color: roomColor }} />
                            </button>
                            <button
                              onClick={() => deleteRoom(room.id)}
                              className="p-1.5 hover:bg-white/80 rounded transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Reorder Users Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-3 sm:p-5 border-b border-gray-200 flex justify-between items-center bg-white rounded-t-2xl z-10">
              <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Organiser l'équipe
              </h2>
              <button onClick={() => setShowOrderModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-3 sm:p-5 overflow-y-auto flex-1">
              <p className="text-sm text-gray-500 mb-4">
                Utilisez les flèches pour modifier l'ordre d'affichage des employés.
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={tempOrder}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {tempOrder.map((userId, index) => {
                      const user = users.find(u => u.id === userId);
                      if (!user) return null;
                      const colorMatch = getUserColor(user.id);

                      return (
                        <SortableUserItem
                          key={userId}
                          id={userId}
                          user={user}
                          index={index}
                          moveUser={moveUser}
                          isFirst={index === 0}
                          isLast={index === tempOrder.length - 1}
                          colorMatch={colorMatch}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div className="p-3 sm:p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={saveOrder}
                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all active:scale-95"
              >
                Sauvegarder l'ordre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                {editingEvent ? 'Modifier' : 'Ajouter'} un événement
              </h2>
              <button
                onClick={() => {
                  setShowEventModal(false);
                  setEventForm({ date: "", title: "" });
                  setEditingEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveEvent} className="p-3 sm:p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label htmlFor="event-date" className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  id="event-date"
                  name="date"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label htmlFor="event-title" className="block text-sm font-medium text-gray-700 mb-2">
                  Titre
                </label>
                <input
                  id="event-title"
                  name="title"
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Ex: Jour férié, Fermeture anticipée..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  maxLength="200"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {eventForm.title.length}/200 caractères
                </p>
              </div>

              <div className="flex gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEventModal(false);
                    setEventForm({ date: "", title: "" });
                    setEditingEvent(null);
                  }}
                  className="flex-1 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-3 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Enregistrement...' : (editingEvent ? 'Modifier' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Viewing Event Modal */}
      {viewingEvent && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setViewingEvent(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 relative animate-in fade-in zoom-in duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingEvent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-100 rounded-2xl">
                <span className="text-2xl"></span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 leading-tight">Événement</h3>
                <p className="text-sm font-medium text-gray-500 mt-0.5">
                  {formatDateSafe(viewingEvent.date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 mb-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <p className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap font-medium">
                {viewingEvent.title}
              </p>
            </div>

            {isTeamLeaderOrAbove && (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setViewingEvent(null);
                    handleEditEvent(viewingEvent);
                  }}
                  className="flex-1 py-2.5 bg-amber-100 text-amber-700 rounded-xl font-bold hover:bg-amber-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setViewingEvent(null);
                  }}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
              </div>
            )}

            {!isTeamLeaderOrAbove && (
              <button
                onClick={() => setViewingEvent(null)}
                className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      )}

      {/* Skipped Shifts Notification Panel */}
      {showSkippedPanel && skippedShiftNotifs.length > 0 && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/20"
            onClick={() => setShowSkippedPanel(false)}
          />
          <div className="fixed top-16 right-4 sm:right-8 z-[61] w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  Shifts non dupliqués ({skippedShiftNotifs.length})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearAllSkippedNotifs}
                  className="text-xs text-amber-700 hover:text-amber-900 font-medium px-2 py-1 rounded hover:bg-amber-100 transition-colors"
                >
                  Tout effacer
                </button>
                <button
                  onClick={() => setShowSkippedPanel(false)}
                  className="p-1 rounded-md hover:bg-amber-100 transition-colors"
                >
                  <X className="w-4 h-4 text-amber-600" />
                </button>
              </div>
            </div>

            {/* Panel Body - sorted by date ascending */}
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
              {[...skippedShiftNotifs]
                .sort((a, b) => {
                  if (!a.date && !b.date) return 0;
                  if (!a.date) return 1;
                  if (!b.date) return -1;
                  return a.date.localeCompare(b.date);
                })
                .map((notif) => (
                  <div key={notif.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {notif.userName && (
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {notif.userName}
                            </p>
                          )}
                          {notif.startTime && notif.endTime && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                              <Clock className="w-3 h-3" />
                              {formatTimeSafe(notif.startTime, { hour: '2-digit', minute: '2-digit' })}
                              {' → '}
                              {formatTimeSafe(notif.endTime, { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {notif.date && (
                          <p className="text-xs text-indigo-500 mt-0.5 font-medium">
                            📅 {formatDateSafe(notif.date + 'T00:00:00', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed italic">
                          {notif.message}
                        </p>
                      </div>
                      <button
                        onClick={() => dismissSkippedNotif(notif.id)}
                        className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        title="Supprimer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {notif.date && (
                      <button
                        onClick={() => goToSkippedShiftDay(notif.date)}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                      >
                        <Calendar className="w-3 h-3" />
                        Aller au jour
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {/* Floating Selection and Clipboard Action Bar */}
      {isTeamLeaderOrAbove && (selectionMode || (copiedShifts && copiedShifts.length > 0 && showClipboardBar)) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border border-gray-200 shadow-2xl rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4 max-w-4xl w-[90%] md:w-auto animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center gap-3">
            {selectionMode ? (
              <>
                <div className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  {selectedShiftIds.length}
                </div>
                <span className="text-sm font-medium text-gray-700">shifts sélectionnés</span>
              </>
            ) : (
              <>
                <div className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  {copiedShifts.length}
                </div>
                <span className="text-sm font-medium text-gray-700">shifts copiés</span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {selectionMode && selectedShiftIds.length > 0 && (
              <>
                {isManager && (
                  <button
                    onClick={handleValidateSelected}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-semibold shadow-md transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Valider
                  </button>
                )}
                <button
                  onClick={handleCopySelected}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-md transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copier
                </button>
                <button
                  onClick={() => setSelectedShiftIds([])}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-xs font-semibold transition-colors"
                >
                  Désélectionner tout
                </button>
              </>
            )}

            {copiedShifts && copiedShifts.length > 0 && (
              <>
                <button
                  onClick={handlePasteShifts}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-md transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Coller sur cette semaine
                </button>
                <button
                  onClick={handleClearClipboard}
                  className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-semibold transition-colors"
                >
                  Vider le presse-papier
                </button>
              </>
            )}
            
            <button
              onClick={() => {
                if (selectionMode) {
                  setSelectionMode(false);
                } else {
                  setShowClipboardBar(false);
                }
              }}
              className="px-3 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl text-xs font-semibold transition-colors"
            >
              {selectionMode ? 'Fermer' : 'Masquer'}
            </button>
          </div>
        </div>
      )}

      {/* Floating Paint Template Action Bar */}
      {isTeamLeaderOrAbove && paintMode && viewMode !== "cards" && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-purple-900 to-indigo-900 text-white border border-purple-800 shadow-2xl rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4 max-w-4xl w-[90%] md:w-auto animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center gap-3">
            <div className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center shadow-lg">
              <Paintbrush className="w-4 h-4 animate-pulse" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-purple-200 font-semibold uppercase tracking-wider">Mode Remplissage Actif</span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold">{paintTemplate.start_time} - {paintTemplate.end_time}</span>
                {paintTemplate.break_duration > 0 && (
                  <span className="text-xs bg-purple-800/80 px-1.5 py-0.5 rounded text-purple-200">
                    Pause: {paintTemplate.break_duration}m
                  </span>
                )}
                {paintTemplate.room_id !== "" && (() => {
                  const room = rooms.find(r => r.id === parseInt(paintTemplate.room_id));
                  return room ? (
                    <span className="text-xs bg-indigo-800/80 px-1.5 py-0.5 rounded text-indigo-200">
                      Salle: {room.name}
                    </span>
                  ) : null;
                })()}
                {paintTemplate.tag_ids && paintTemplate.tag_ids.length > 0 && (
                  <div className="flex gap-1">
                    {paintTemplate.tag_ids.map(tagId => {
                      const tag = tags.find(t => t.id === tagId);
                      return tag ? (
                        <span
                          key={tag.id}
                          className="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                          style={{ backgroundColor: tag.color, color: '#ffffff' }}
                        >
                          {tag.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-xs text-purple-200 mr-2">
              Cliquez sur les cases du tableau pour peindre ce shift
            </span>
            <button
              onClick={() => setShowPaintConfigModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-semibold shadow-md transition-colors"
            >
              Modifier le tampon
            </button>
            <button
              onClick={() => setPaintMode(false)}
              className="px-3 py-2 bg-indigo-800 hover:bg-indigo-700 text-indigo-200 rounded-xl text-xs font-semibold transition-colors"
            >
              Quitter
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!leaveToDelete}
        variant="danger"
        title="Supprimer ce congé ?"
        message="Le solde de congés sera restitué le cas échéant. Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        loading={deletingLeave}
        onConfirm={confirmDeleteLeave}
        onCancel={() => !deletingLeave && setLeaveToDelete(null)}
      />

      <WeatherDetailModal
        open={weatherModalState.show}
        onClose={() => setWeatherModalState({ show: false, dayWeather: null })}
        dayWeather={weatherModalState.dayWeather}
        currentLocation={weatherLocation}
        onLocationChange={(newLoc) => setWeatherLocation(newLoc)}
      />
    </div>
  );
}
